import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { useEffect, useState, useMemo, useCallback, lazy, Suspense, useRef } from 'react';
import { useTimerBreakMonitor } from '@/hooks/useTimerBreakMonitor';
import { useOptiFinishPushback } from '@/hooks/useOptiFinishPushback';
import { TopBar } from '@/components/layout/TopBar';
import { StatCard } from '@/components/dashboard/StatCard';
import { TechnicianColumn } from '@/components/technicians/TechnicianColumn';
// W5 (field test 2026-10-16): open unattended operations, read once for every column; the vehicle-switch offer.
import { useOpenUnattendedOperations } from '@/hooks/useOpenUnattendedOperations';
import { useUnattendedOperationFlags } from '@/hooks/useUnattendedOperationFlags';
import { attachUnattendedFlags } from '@/lib/timer/unattendedFlags';
import { WorkSwitchHost } from '@/components/technicians/workspace/WorkSwitchHost';
import { TicketCard } from '@/components/tickets/TicketCard';
import { CompletedTicketsSection } from '@/components/dashboard/CompletedTicketsSection';
import { DayboardScrollArea } from '@/components/layout/DayboardScrollArea';
import { TimeClockButton } from '@/components/timeclock/TimeClockButton';
import { StaffPresenceIndicator } from '@/components/dashboard/StaffPresenceIndicator';
import { OBLIBucket } from '@/components/dashboard/OBLIBucket';
import { supabase } from '@/integrations/supabase/client';
import { logAdvisorOverride } from '@/utils/ticketEventLogger';
import {
  retainDayboardBroadcastChannel,
  notifyDayboardInvalidated,
} from '@/utils/dayboardInvalidation';
import { CheckCircle2, Clock, Users, AlertTriangle, Car, Wrench, LayoutDashboard, List, Map as MapIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { OpenCapacityPanel } from '@/components/capacity/OpenCapacityPanel';
import { NeedsAttentionPanel } from '@/components/operations/NeedsAttentionPanel';
import { LiveUpdatesBanner, nextLiveState, ScheduleReconcileBanner, type LiveState } from '@/components/dashboard/LiveUpdatesBanner';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, DragOverEvent, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useToast } from '@/hooks/use-toast';
import { useDate } from '@/contexts/DateContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz, shopLocalToUtc, getShopDayLabel, getTechDayHours } from '@/utils/shopTime';
import { getTodaySafeStartTime } from '@/utils/getTodaySafeStartTime';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
// DMC-D3c-1: canonical deleted-state helper. See
// src/utils/ticketDeletedState.ts; mirrors the SQL helpers
// public.is_ticket_deleted / public.is_ticket_active from migration
// 20260508120000_dmc_d2_deleted_state_helpers.sql.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
// Phase G1 — canonical workload helpers. Overview Technician Workload cards
// must produce the same minutes as the Dayboard tech-column header for the
// same tech + selected date + ticket set. Reuse the helper; do not duplicate
// any time math in this file.
import { getDerivedTicketTime, sumWorkloadMinutes } from '@/utils/derivedTicketTime';

/**
 * For same-day drag/drop, returns the later of shift start or now+1min (UTC ISO).
 * Prevents "Cannot schedule in the past" when shift start has already passed.
 */
// R33: getTodaySafeStartTime extracted to '@/utils/getTodaySafeStartTime'
// so it can be unit-tested in isolation with clock.setFixed(...).
import { calculateWallMinutes } from '@/utils/canonicalTimeMath';
import { formatShopTime } from '@/utils/formatShopTime';
import { format, getDay, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAutoBump } from '@/hooks/useAutoBump';
// R3-A Option B: the server reconciles Run Long timing; the board asks it on open / reconnect and says when it is stale.
import { useRunLongReconcileOnOpen } from '@/hooks/useRunLongReconcileOnOpen';
import { publishScheduleFreshness } from '@/lib/scheduling/serverEodState';
import { mayResumeReadyInterruptedRepair } from '@/lib/scheduling/chainOwnership';
import { useLiveReshuffle } from '@/hooks/useLiveReshuffle';
import { useTicketCustodyRealtime } from '@/hooks/tickets/useTicketCustodyRealtime';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useBoardAdvisorBayHold } from '@/hooks/tickets/useBoardAdvisorBayHold';
import {
  buildSplitPartLabelDataMap,
  type SplitGroupTicketShape,
} from '@/utils/splitGroupIdentity';
import { adjustForBreaks } from '@/utils/breakAdjustment';
import { parkCompletedTicket } from '@/utils/parkingHelpers';
import { useSortedTechnicians, getSortedTechnicians } from '@/hooks/useSortedTechnicians';
import { NotificationPanel, Notification } from '@/components/layout/NotificationPanel';
import { ImportReport, ImportReportData } from '@/components/tickets/ImportReport';
import { EODRolloverContainer } from '@/components/tickets/EODRolloverContainer';
import { RolloverDecisionRequiredStrip } from '@/components/scheduling/RolloverDecisionRequiredStrip';
import { useConvertedRolloverGrowth } from '@/hooks/useConvertedRolloverGrowth';
import { EODBufferToast } from '@/components/tickets/EODBufferToast';
import { WaiterBumpWarningContainer } from '@/components/tickets/WaiterBumpWarningContainer';
import { SuggestionsDrawer } from '@/components/optimizer/SuggestionsDrawer';
import { OptimizerStatus } from '@/components/optimizer/OptimizerStatus';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { safeValidateTicketUpdate } from '@/utils/ticketUpdateValidation';
import { getTechAssignedMinutes } from '@/utils/technicianCapacity';
import {
  resolveTechnicianCapacity,
  capacityUtilizationPercent,
  describeCapacity,
} from '@/utils/capacity/technicianCapacityState';
import { describeCapacityBasis } from '@/utils/efficiencyDisclosure'; // Core-100 R4 W5 (CORE-AW Prov)
import { withAbsenceIntervals } from '@/utils/capacity/absenceIntervals';
import { useTechnicianAbsenceIntervals } from '@/hooks/useTechnicianAbsenceIntervals';
import { isTechnicianScheduledPresent } from '@/utils/technicianPresence';
import { WelcomeDialog } from '@/components/onboarding/WelcomeDialog';
import { NoAccess } from './NoAccess';
import { useFormFactor } from '@/hooks/use-mobile';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from '@/components/ui/carousel';
import { dayboardColumnAccess } from '@/lib/roles/dayboardColumnAccess';
import { useCarouselEdgeScroll } from '@/hooks/useCarouselEdgeScroll';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Badge } from '@/components/ui/badge';
import { WeatherAlertBanner } from '@/components/weather/WeatherAlertBanner';
import { WeatherAlertPanel } from '@/components/weather/WeatherAlertPanel';
import { useWeatherAlerts } from '@/hooks/useWeatherAlerts';
import { ReminderCountsProvider } from '@/contexts/ReminderCountsContext';
import { WeatherPill } from '@/components/weather/WeatherPill';
import { DayboardLoadIndicator } from '@/components/dashboard/DayboardLoadIndicator';
import { mapShopHoursToDayNumbers } from '@/lib/shopHours/mapCalendarHours';
import { useMinuteProductivityMetrics } from '@/hooks/useMinuteProductivityMetrics';
import { ShopUtilizationCard } from '@/components/dashboard/ShopUtilizationCard';
import { FinancialSnapshotCard } from '@/components/dashboard/FinancialSnapshotCard';
import { RequireFeature } from '@/components/access/RequireFeature';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { TeamFilter } from '@/components/teams/TeamFilter';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ParkingLotMapView } from '@/components/parking-lot';
import { ParkingSpotPickerDialog } from '@/components/birdseye/ParkingSpotPickerDialog';
// SCH-4 (K50): manual cross-tech move governance + preference-learning reason.
import { MoveReasonDialog } from '@/components/scheduling/MoveReasonDialog';
import type { MoveReason } from '@/lib/moveReasonConstants';
import { manualMoveRequiresReason, DEFAULT_OPERATIONAL_MOVE_REASON } from '@/lib/scheduling/moveReasonGate';
// DTC-G G2: route the past-time guard for slot scheduling through the
// canonical clock so the validation business decision is deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { logRealtimeStatus, isAbnormalRealtimeClose } from '@/utils/realtimeStatusLog';
import { isOnBacklogBoard } from '@/utils/backlogState';
import { useTicketDeepLinkDay } from '@/hooks/useTicketDeepLinkDay';
import { needsDeepLinkCommandCenterHost } from '@/lib/tickets/ticketDeepLinkDay';
import { useMechanicalRoster, useMechanicalTechnicianIds } from '@/hooks/useMechanicalTechnicianIds';
import { isDayboardOverlayLocked } from '@/lib/ui/dayboardOverlayLock';
import { dayboardCardVehicle } from '@/lib/dayboard/dayboardCardVehicle';
import { TicketDeepLinkFallback } from '@/components/lifecycle/TicketDeepLinkFallback';
import { AbsentTechReassignReview } from '@/components/technicians/AbsentTechReassignReview';

const SpecialtyBays = lazy(() => import('@/pages/SpecialtyBays'));

/**
 * Batch 10: structured PostgREST error logger for the ticket-query
 * chain. Captures the four-field shape supabase-js returns on
 * `.error` (code / message / details / hint) so a 500 pinpoints
 * the offending column / relation / RLS rather than vanishing
 * silently. Each call site labels its `stage` so the console line
 * tells the operator which query failed.
 *
 * `context` carries non-sensitive query scope — the selected day label
 * for date-scoped fetches, the ticket id for single-row refreshes. A
 * PGRST200 schema-cache failure is date-independent, so seeing the same
 * error under two different `selectedDate` values is itself the signal
 * that the date is NOT the variable at fault. Never pass customer data.
 */
function logDashboardSupabaseError(
  stage: string,
  err: unknown,
  context?: Record<string, string | null | undefined>,
): void {
  const e = (err ?? {}) as { code?: string; message?: string; details?: string; hint?: string };
  console.error(`[Dashboard] ${stage} load failed`, {
    operation: stage,
    ...(context ?? {}),
    code: e.code ?? null,
    message: e.message ?? null,
    details: e.details ?? null,
    hint: e.hint ?? null,
  });
}

const Dashboard = () => {
  const { toast } = useToast();
  const { selectedDate, selectedDayLabel } = useDate();
  const { setSelectedDayLabel } = useDate();   // a deep link selects its ticket's day
  const { shopTimezone, isTimezoneValid } = useShopTimezone();
  // NOV3 (A3): on TODAY's board, work still RUNNING on an earlier day (a stale timer) is shown so it can be paused or
  // stopped; every other row stays on its own day.
  const isEarlierDayRunningOnToday = (t: { scheduled_date?: string | null; timer_status?: string | null }) =>
    selectedDayLabel === getTodayInShopTz(shopTimezone) && t.timer_status === 'running'
    && !!t.scheduled_date && t.scheduled_date < selectedDayLabel;
  const formFactor = useFormFactor();
  const isMobile = formFactor === 'phone';
  const queryClient = useQueryClient();
  
  // BUG-8 reorder: useAuth must come before hook calls that need activeShop.id
  const { activeShop, membership, user, userShops, isShopDataLoading, isLoading: authLoading } = useAuth();
  // Whose time is MECHANICAL repair capacity (work roles) — shared by the
  // load indicator, OBLI and the Calendar so a detail-only specialist never
  // inflates repair capacity.
  const mechanicalTechnicianIds = useMechanicalTechnicianIds(activeShop?.id);
  const mechanicalRoster = useMechanicalRoster(activeShop?.id);
  const permissions = usePermissions();
  // OCT28 #1: the card shows plate + state only to viewers of the vehicle-identity rule (canViewVehicleIdentity); a ref so the
  // realtime mappers (memoised on the timezone) read the current answer.
  const showPlateRef = useRef(false);
  showPlateRef.current = permissions.canViewVehicleIdentity;
  const { canViewOverview, isOwner, isManager, isServiceWriter, canDragDropTickets, isTechnician, isDetailSpecialist, canAssignTickets } = permissions;
  // Multi-role column access (see dayboardColumnAccess): a worker without dispatch authority is
  // limited to their own column; an Owner/Manager/Advisor who also works on vehicles is not.
  const { isFloorWorker, restrictToOwnColumn } = dayboardColumnAccess({ isTechnician, isDetailSpecialist, canAssignTickets });
  // Core-100 CORE-BO: the Open Capacity panel (people who can assign work).
  const [openCapacityOpen, setOpenCapacityOpen] = useState(false);
  // Core-100 R3: unexpected arrivals, open timers and running-long visits, in front of the people who act on them.
  const [needsAttentionOpen, setNeedsAttentionOpen] = useState(false);
  // WS-ABSENT: the absent technician whose tickets a person is reviewing (see handleReassignNeeded).
  const [absentReview, setAbsentReview] = useState<{ tickets: any[]; technicianName: string } | null>(null);
  // Core-100 R4 (CORE-AD): a stale board is a hazard — say so while the realtime channel is down.
  const [liveState, setLiveState] = useState<LiveState>({ live: true });
  // Core-100 R4 X4 (CORE-AD Rel): once the channel was lost, the rejoin itself re-reads the board — changes made
  // while it was down were never delivered (Phoenix does not replay them).
  const liveLostRef = useRef(false);
  // R3-A Option B (founder requirement 4): reconcile the server's Run Long schedule the moment the board opens,
  // returns to view, comes back online, or recovers its realtime channel; never present the pre-reconcile picture as live.
  const runLongReconcile = useRunLongReconcileOnOpen(activeShop?.id ?? null);
  const reconcileNowRef = useRef(runLongReconcile.reconcileNow);
  reconcileNowRef.current = runLongReconcile.reconcileNow;
  // OCT28 WP-2b-2b-ii-b2-A: the ticket EOD checks read THIS freshness (the one status poll), never a second one
  useEffect(() => {
    publishScheduleFreshness(activeShop?.id ? {
      shopId: activeShop.id,
      pending: runLongReconcile.pending,
      stale: runLongReconcile.stale,
      failing: runLongReconcile.failing === true,
      lastReconciledAt: runLongReconcile.lastReconciledAt,
    } : null);
  }, [activeShop?.id, runLongReconcile.pending, runLongReconcile.stale, runLongReconcile.failing, runLongReconcile.lastReconciledAt]);
  useEffect(() => () => publishScheduleFreshness(null), []);

  // C-1: Compute BEFORE any useState/useEffect/useMemo — never early-return between hooks
  const shouldShowNoAccess = !authLoading && !isShopDataLoading && userShops.length === 0;

  // Monitor timers during breaks (BUG-7: pass selectedDayLabel + shopTimezone, not Date)
  // R5-H-2: Pass shopId to scope break monitor queries to current shop
  useTimerBreakMonitor(selectedDayLabel, shopTimezone, activeShop?.id ?? null);
  
  // Push back opti_finish when timers run past scheduled finish time
  // BUG-8: Pass shopId to prevent cross-shop ticket processing
  useOptiFinishPushback(selectedDayLabel, activeShop?.id ?? null);

  const [technicians, setTechnicians] = useState<any[]>([]);
  // Whose roster `technicians` currently holds, and whether it loaded. The
  // load index divides by this roster's capacity, so an empty array from a
  // read that has not finished — or failed, or belongs to the previous shop —
  // must never be read as "nobody works today".
  const [technicianRoster, setTechnicianRoster] = useState<{ shopId: string | null; status: 'loading' | 'ready' | 'error' }>({ shopId: null, status: 'loading' });
  const [tickets, setTickets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [myTechId, setMyTechId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTicket, setActiveTicket] = useState<any | null>(null);
  const [activeTechnician, setActiveTechnician] = useState<any | null>(null);
  const [dragOverTechId, setDragOverTechId] = useState<string | null>(null);
  const [previewStartTime, setPreviewStartTime] = useState<string | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number>(-1);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // CORE-02 FIX: Refs to access current drag state inside realtime handlers (avoid stale closures)
  const isDraggingRef = useRef(isDragging);
  const activeTicketRef = useRef(activeTicket);
  const inFlightReassignIds = useRef<Set<string>>(new Set());
  // R37: Per-ticket monotonic version counter for the realtime → REST refetch
  // race window. Each realtime UPDATE/INSERT for ticket X stamps an
  // incrementing version BEFORE the supabase.from('tickets').select(...) call;
  // when the fetch resolves, we drop the result if a newer version has been
  // stamped (i.e., a later UPDATE arrived first). Prevents an out-of-order
  // resolution from overwriting fresh state with a stale snapshot.
  const realtimeFetchVersions = useRef<Map<string, number>>(new Map());

  // Keep refs in sync with state
  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    activeTicketRef.current = activeTicket;
  }, [activeTicket]);
  const [technicianOrder, setTechnicianOrder] = useState<string[]>([]);
  
  // Bug 3.1 fix: Load technician order after shop ID is available
  useEffect(() => {
    const shopId = activeShop?.id || membership?.shop_id;
    if (!shopId) return;

    const key = `technician-order-${shopId}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setTechnicianOrder(parsed);
      } catch (e) {
        console.warn('[TECH-ORDER] Failed to parse saved technician order', e);
      }
    }
  }, [activeShop?.id, membership?.shop_id]);
  const [techHoverIndex, setTechHoverIndex] = useState<number>(-1);
  const [compactView, setCompactView] = useState(() => {
    const saved = localStorage.getItem('dayboard-compact-view');
    return saved ? JSON.parse(saved) : false;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showImportReport, setShowImportReport] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ImportReportData | null>(null);
  const [showParkingLotDrawer, setShowParkingLotDrawer] = useState(false);
  const [parkingLotView, setParkingLotView] = useState<'list' | 'map'>('list');
  const [showWeatherAlerts, setShowWeatherAlerts] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [shopHours, setShopHours] = useState<Record<number, { open: string; close: string }>>({});
  const [holidays, setHolidays] = useState<string[]>([]);
  // L-3: time_clock_enabled fetched once per shop mount in Effect A, passed as prop to TechnicianColumns
  const [timeClockEnabled, setTimeClockEnabled] = useState<boolean>(false);
  // Bucket 5 §5.5.4 — Slice 0.3b: shop_culture.no_show_grace_threshold_min,
  // fetched in the same Effect A as time_clock_enabled (single shop_profile
  // round-trip per shop mount) and passed to TechnicianColumns. Display-only.
  const [noShowGraceThresholdMin, setNoShowGraceThresholdMin] = useState<number>(15);
  
  // Track new OptiQueue ticket IDs for flashing red border
  const [newOptiQueueTicketIds, setNewOptiQueueTicketIds] = useState<Set<string>>(new Set());
  const [staffRefreshKey, setStaffRefreshKey] = useState(0);
  const [timeClockRefreshKey, setTimeClockRefreshKey] = useState(0);
  
  // Shared weather alerts — one subscription for both banner and panel
  const { openAlerts: weatherAlerts, dismissAlert, resolveAlert, isLoading: weatherLoading } = useWeatherAlerts();
  
  // Multi-team support - team filter for Dayboard
  const { isMultiTeamEnabled } = useMultiTeam();
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  
  // Optimistic drag-and-drop state
  const [pendingMoveTicketId, setPendingMoveTicketId] = useState<string | null>(null);
  const [originalTicketData, setOriginalTicketData] = useState<any | null>(null);

  // SCH-4: deferred manual cross-tech move awaiting an operator reason. The
  // action thunks live in a ref (synchronous — avoids a confirm/cancel race
  // with the dialog's onOpenChange); meta drives the dialog's display.
  const pendingReasonMoveRef = useRef<{
    run: (reason: MoveReason) => Promise<void>;
    cancel: () => void;
  } | null>(null);
  const [moveReasonOpen, setMoveReasonOpen] = useState(false);
  const [moveReasonMeta, setMoveReasonMeta] = useState<{
    fromTechName?: string;
    toTechName?: string;
    ticketLabel?: string;
  } | null>(null);

  // Refresh a single ticket's data (used after job edits/adds/removals)
  const refreshSingleTicket = useCallback(async (ticketId: string) => {
    const { data: updatedTicket, error: refreshErr } = await supabase
      .from('tickets')
      .select(`*, repair_number, vehicle:vehicles(id, year, make, model, engine_size, license_plate, plate_jurisdiction), ticket_jobs(minutes_override, notes, separate_time, diagnostic_completed_at, actual_minutes, job_status, job:jobs(*)), technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end), completed_technician:technicians!completed_by_tech_id(id, name)`)
      .eq('id', ticketId)
      .single();

    if (refreshErr) {
      logDashboardSupabaseError('refresh_single_ticket', refreshErr);
    }

    if (updatedTicket) {
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { 
          ...updatedTicket,
          jobs: updatedTicket.ticket_jobs?.map((tj: any) => ({
            ...tj.job,
            standardMinutes: tj.minutes_override || tj.job.standard_minutes,
            notes: tj.notes,
            // Match the canonical job-shape used by the date-scoped fetch
            // path below (L988-991). Pre-fix these four fields were
            // omitted from initializeData's realtime SELECT, so any
            // realtime-driven setTickets after a manual refresh would
            // drop diagnostic-complete state / job-status / actual-minutes
            // overrides from the local ticket — the card visibly
            // regressed to non-completed state until the next date toggle.
            separateTime: tj.separate_time || false,
            diagnosticCompletedAt: tj.diagnostic_completed_at,
            actualMinutes: tj.actual_minutes,
            jobStatus: tj.job_status,
          })) || [],
          vehicle: dayboardCardVehicle(updatedTicket.vehicle, { showPlate: showPlateRef.current }),
          vehiclePresent: isVehiclePresent(updatedTicket),
          predictedMinutes: updatedTicket.predicted_minutes,
          assignedTechId: updatedTicket.assigned_tech_id,
          pauseReason: updatedTicket.pause_reason,
          startTime: formatShopTime(updatedTicket.start_time, shopTimezone, 'short'),
          completed_by_name: (updatedTicket as any).completed_technician?.name || null,
        } : t
      ));
    }
  }, [shopTimezone]);

  // Page-level completion parking spot prompt (survives TicketCard unmount)
  const [completionParkingPrompt, setCompletionParkingPrompt] = useState<{ ticketId: string; shopId: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolveMyTechId = async () => {
      if (!isFloorWorker || !user?.id || !activeShop?.id) {
        setMyTechId(null);
        return;
      }

      const linkedTechnician = technicians.find((tech) => tech.user_id === user.id);
      if (linkedTechnician?.id) {
        setMyTechId(linkedTechnician.id);
        return;
      }

      const { data, error } = await supabase
        .from('technician_users')
        .select('technician_id')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.warn('[Dashboard] Failed to resolve technician mapping for current user:', error);
        setMyTechId(null);
        return;
      }

      setMyTechId(data?.technician_id ?? null);
    };

    resolveMyTechId();

    return () => {
      cancelled = true;
    };
  }, [activeShop?.id, isFloorWorker, technicians, user?.id]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.ticketId && detail?.shopId) {
        console.log('[Dashboard] parking-spot-prompt received for ticket:', detail.ticketId);
        setCompletionParkingPrompt({ ticketId: detail.ticketId, shopId: detail.shopId });
      }
    };
    window.addEventListener('parking-spot-prompt', handler);
    return () => window.removeEventListener('parking-spot-prompt', handler);
  }, []);

  // MED 1.2: Single source of truth for technician sorting/filtering
  // Shop-wide visibility: technicians see ALL columns (interaction restricted elsewhere)
  // OBLI capacity is a measurement only when THIS shop's roster loaded and the
  // work-role answer is known (not loading, not refused) — otherwise the
  // indicator says "Capacity unavailable" instead of dividing by zero.
  // Core-100 R4 (CORE-AW): partial-day absences are capacity input. They are attached to each technician
  // for the selected day; until they are known the capacity is not presented as known (an unreadable
  // absence list could only overstate availability).
  const absenceIntervals = useTechnicianAbsenceIntervals(activeShop?.id, selectedDayLabel, selectedDayLabel);
  const capacityTechnicians = useMemo(
    () => withAbsenceIntervals(technicians, absenceIntervals.byTech),
    [technicians, absenceIntervals.byTech],
  );
  const obliCapacityInputsReady = technicianRoster.status === 'ready'
    && technicianRoster.shopId === activeShop?.id
    && mechanicalRoster.status === 'known'
    && absenceIntervals.status === 'known';

  const sortedTechnicians = useSortedTechnicians(technicians, {
    selectedDate,
    selectedDayLabel,
    technicianOrder,
  });

  // Enable carousel edge scrolling when dragging on phone/tablet
  useCarouselEdgeScroll({
    api: carouselApi,
    isDragging: isDragging && (formFactor === 'phone' || formFactor === 'tablet'),
    edgeThreshold: 60,
    scrollInterval: 500,
  });

  // Phone/tablet: a technician opens the Dayboard on THEIR OWN column. The carousel otherwise always
  // starts at the first technician, so in a 25-technician shop someone had to swipe past everyone
  // else to find their work. Done once per carousel instance (form factor change re-creates it), so
  // it never fights the person's own swipes afterwards.
  const ownColumnScrolledRef = useRef<CarouselApi | null>(null);
  useEffect(() => {
    if (!carouselApi || !myTechId) return;
    if (formFactor !== 'phone' && formFactor !== 'tablet') return;
    if (ownColumnScrolledRef.current === carouselApi) return;
    const index = sortedTechnicians.findIndex((t) => t.id === myTechId);
    if (index < 0) return;
    ownColumnScrolledRef.current = carouselApi;
    carouselApi.scrollTo(index, true);
  }, [carouselApi, myTechId, formFactor, sortedTechnicians]);

  // Handler to reinit carousel after kebab menu moves (fixes scroll lock on mobile)
  const handleKebabMenuReassign = () => {
    // Small delay to let React Query refetch complete before reinit
    setTimeout(() => {
      carouselApi?.reInit();
    }, 100);
  };

  // Toggle compact view
  const toggleCompactView = () => {
    setCompactView(prev => {
      const newValue = !prev;
      localStorage.setItem('dayboard-compact-view', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState<string>(() => {
    const tab = searchParams.get('tab');
    // Everyone defaults to 'dayboard'
    if (!tab) {
      return 'dayboard';
    }
    return tab;
  });
  const [highlightedTicketId, setHighlightedTicketId] = useState<string | null>(null);

  // A deep link names a ticket; the Dayboard only renders the selected day.
  // Select the ticket's OWN shop day first (or offer the fallback when no
  // Dayboard date can show it) — never strand the advisor on whatever date
  // they happened to be viewing.
  const deepLinkTicketId = searchParams.get('highlight');
  const deepLinkWantsDetails = searchParams.get('openDetails') === 'true';
  const { fallback: deepLinkFallback, clearFallback: clearDeepLinkFallback } =
    useTicketDeepLinkDay(deepLinkTicketId, selectedDayLabel, setSelectedDayLabel, deepLinkWantsDetails);

  /** Drop this deep link's highlight / openDetails (only if it is still the current one). */
  const clearHighlightParams = (id: string) => {
    setHighlightedTicketId((cur) => (cur === id ? null : cur));
    const current = new URLSearchParams(window.location.search);
    if (current.get('highlight') !== id) return;
    current.delete('highlight');
    current.delete('openDetails');
    setSearchParams(current, { replace: true });
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    const resolvedTab = tab || 'dayboard';
    setCurrentTab(resolvedTab);
    
    // Check for highlight parameter
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedTicketId(highlightId);
      // The highlight is cleared once the card has been shown (see the scroll
      // effect below) — after a day switch the card mounts later than the
      // link arrives. This is only the safety net if it never appears.
      setTimeout(() => clearHighlightParams(highlightId), 20000);
    }
    
    // Load notifications from localStorage
    const stored = localStorage.getItem('notifications');
    if (stored) {
      const parsed = JSON.parse(stored);
      setNotifications(parsed.map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp)
      })));
    }
  }, [searchParams, setSearchParams]);

  // Scroll to highlighted ticket once it's loaded and rendered
  useEffect(() => {
    if (highlightedTicketId && !isLoading) {
      // Check if the highlighted ticket exists in loaded tickets
      const ticketExists = tickets.some(t => t.id === highlightedTicketId);

      if (ticketExists) {
        // Small delay to ensure DOM is rendered after state update
        setTimeout(() => {
          const ticketElement = document.getElementById(`ticket-${highlightedTicketId}`);
          if (ticketElement) {
            ticketElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        // Clear highlight after the animation (1.5s × 3 + buffer), counted
        // from when the card is actually on the board.
        const id = highlightedTicketId;
        const t = setTimeout(() => clearHighlightParams(id), 6500);
        return () => clearTimeout(t);
      }
    }
    return undefined;
  }, [highlightedTicketId, isLoading, tickets]);

  // Enable auto-bump system for today's tickets
  const handleTicketUpdate = () => {
    // Trigger a refresh when auto-bump updates tickets. Routes through
    // the centralized cross-device invalidation helper so other devices
    // viewing this shop also refetch. See src/utils/dayboardInvalidation.ts.
    void notifyDayboardInvalidated({
      reason: 'autobump',
      shopId: activeShop?.id || '',
    });
  };
  
  useAutoBump({
    enabled: selectedDayLabel === getTodayInShopTz(shopTimezone) && !isDragging,
    selectedDayLabel,
    shopTimezone,
    shopId: activeShop?.id ?? null,  // H-1: Pass shopId for query scoping
    onTicketUpdate: handleTicketUpdate,
  });

  // Enable live reshuffle with event-driven updates (alongside 60s auto-bump)
  useLiveReshuffle({
    shopId: activeShop?.id || null,
    selectedDayLabel,
    enabled: !isDragging, // Disable during drag operations
  });

  // Lifecycle v2 — slice 4A.2. Keep TicketCard custody chips live when
  // another user changes custody on this shop. Strictly invalidation:
  // does not touch scheduling, timer, parking, rollover, or bay caches.
  // No-op when the flag is off; see useTicketCustodyRealtime for the
  // shop-id defence-in-depth guard.
  const dashboardCustodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  useTicketCustodyRealtime(activeShop?.id ?? null, dashboardCustodyHandoffEnabled);

  // Unified rollover system: live growth hook for converted rollovers
  useConvertedRolloverGrowth();

  // M-2 Effect A — Shop-scoped (runs ONLY when shop changes, NOT on date change)
  // Contains: seed gate, tech/hours fetch, tutorial trigger.
  // Does NOT create channels — so date switching never causes tech/shop refetches.
  useEffect(() => {
    const initializeData = async () => {
      try {
        console.log('Starting data initialization...');
        
        // Core-100 R3 (CORE-U / data authority): the board used to call a demo "seedDatabase" here, which —
        // whenever no technician was visible (every new shop) — inserted MOCK technicians and a mock shop
        // profile into a fixed demo shop id. RLS normally refused it; for a member of that shop it fabricated
        // people. A shop's people come from its owner, never from fixtures, so the call is gone.
        
        // Load technicians and tickets
        if (!activeShop) return;
        
        // A ONE-TIME DATA REPAIR USED TO RUN HERE, AND IT DELETED PEOPLE.
        //
        // `cleanupDuplicateTechnicians` grouped the shop's `technicians` rows by
        // `name`, kept one, and DELETEd the rest — on every board load. It was
        // written for a single historical duplicate and never removed, so two
        // employees called Mike Smith meant one of them was destroyed the next
        // time a manager opened the Dayboard, taking their user binding and
        // every timer session they had ever run with them (both CASCADE).
        //
        // A display name is not an identity. Worker identity lives in the
        // membership, the `technician_users` binding and the professional
        // identity, and any future remediation must be an explicit, identity-
        // based, dry-runnable admin action — never something a page does while
        // you look at it.
        //
        // Removed in full; the utility no longer exists. See
        // `tests/e2e/duplicateTechnicianSafety.parity.spec.ts` and
        // `tests/unit/noDestructiveWorkerCleanup.test.ts`.
        
        // Fetch technicians with user links (only active technicians for scheduling)
        setTechnicianRoster({ shopId: activeShop.id, status: 'loading' });
        const { data: techData, error: techError} = await supabase
          .from('technicians')
          .select(`
            *,
            technician_users(user_id)
          `)
          .eq('shop_id', activeShop.id)
          .eq('employment_status', 'Active');
        if (techError) {
          console.error('Error loading technicians:', techError);
          setTechnicianRoster({ shopId: activeShop.id, status: 'error' });
          throw techError;
        }
        
        console.log('Loaded technicians:', techData?.length);
        
        // Fetch shop profile for hours, holidays, and feature flags.
        // Bucket 5 §5.5.4 — Slice 0.3b: also pull `shop_culture` so the
        // No-Show Risk grace threshold can be lifted to TechnicianColumns
        // without per-column queries.
        const { data: shopProfile } = await supabase
          .from('shop_profile')
          .select('hours, holidays, time_clock_enabled, shop_culture')
          .eq('shop_id', activeShop.id)
          .maybeSingle();
        
        if (shopProfile) {
          // Parse hours into {0: {open, close}, ...} for the day-number
          // lookups OBLIBucket + DayboardLoadIndicator perform.
          //
          // Case-tolerant via the canonical `mapShopHoursToDayNumbers`
          // helper. The inline map this replaces keyed CAPITALIZED day
          // names only, while `commitWizard` persists
          // `shop_profile.hours` with LOWERCASE keys (see
          // `commitWizard.ts:54` doctrine comment). On every
          // wizard-onboarded shop every entry was dropped, `shopHours`
          // stayed `{}` — truthy, so `DayboardLoadIndicator`'s
          // `if (!shopHours) return true` escape hatch did not fire —
          // and the Dayboard rendered a "Shop Closed" badge for every
          // date, contradicting Shop Profile > Hours & Holidays (which
          // reads the same row case-tolerantly and correctly showed the
          // days as open). Saving the profile form once masked the bug
          // by rewriting the row with capitalized keys.
          //
          // Same helper + same root cause as the CalendarModal fix
          // (`lib/shopHours/mapCalendarHours.ts`) and the TopBar
          // date-stepper fix (`lib/shopHours/dayStepper.ts`).
          const mappedHours = mapShopHoursToDayNumbers(shopProfile.hours ?? {});

          setShopHours(mappedHours);
          
          // Parse holidays - array of date strings
          const holidayList = Array.isArray(shopProfile.holidays) ? shopProfile.holidays.map((h: any) => h.date || h) : [];
          setHolidays(holidayList);

          // L-3: Store time_clock_enabled once per shop mount — not per-column
          setTimeClockEnabled((shopProfile as any).time_clock_enabled ?? false);

          // Bucket 5 §5.5.4 — Slice 0.3b: read no-show grace threshold from
          // shop_culture; default to 15 minutes when missing. Display-only.
          const culture = (shopProfile as any).shop_culture as Record<string, unknown> | null;
          const rawThreshold = culture?.no_show_grace_threshold_min;
          const parsedThreshold = typeof rawThreshold === 'number' && Number.isFinite(rawThreshold) && rawThreshold >= 0
            ? rawThreshold
            : 15;
          setNoShowGraceThresholdMin(parsedThreshold);
        }

        if (techData) {
          // Add user_id from technician_users to each technician
          const techsWithUserId = techData.map(tech => ({
            ...tech,
            user_id: tech.technician_users?.[0]?.user_id || null,
          }));
          setTechnicians(techsWithUserId);
          setTechnicianRoster({ shopId: activeShop.id, status: 'ready' });
        }
        // MED 2.1: Ticket fetch removed from init — date-change effect is sole ticket fetcher
        setIsLoading(false);
        // ONB-R69-FIX (HIGH-1): tutorial-start moved to a dedicated effect
        // below so it re-runs when `membership` resolves. Previously this
        // block ran inside initializeData whose dep array only contained
        // `[activeShop?.id, shopTimezone]`; if `membership` (or tutorialLoading)
        // hadn't resolved by the time shop data loaded, the `if (!tutorialLoading
        // && membership)` check failed, `localStorage.removeItem` never ran,
        // and the tutorial never started on the fresh-shop landing — a
        // guaranteed-broken "5-minute first win" for every slow network.
      } catch (err: any) {
        console.error('Failed to initialize data:', err);
        setError(err.message || 'Failed to load data');
        setIsLoading(false);
      }
    };

    initializeData();
  }, [activeShop?.id, shopTimezone]); // M-2: Shop-scoped only — date change does NOT re-run this

  // CORE-BL: first-use delivery does NOT live here any more.
  //
  // The effect that used to sit here was ONB-R69-FIX (HIGH-1) — an
  // auto-start keyed on `membership` + `activeShop` + `tutorialLoading`.
  // Its ordering fix was real and is preserved by the coordinator, which
  // waits on the same three facts through `TutorialContext` rather than
  // re-reading them.
  //
  // It was a Dashboard-local effect, which meant the one automatic invitation
  // was only ever delivered to people whose first landing was the dashboard.
  // `App.tsx` guards `/` to OWNER, MANAGER, SERVICE_WRITER and TECHNICIAN, so
  // for a dispatcher, parts person, auditor, org admin, region manager or
  // district manager the deciding code never mounted and their flag sat
  // unread. Writing role content would not have fixed that; the delivery
  // point was unreachable.
  //
  // It now lives in `FirstUseOnboardingCoordinator`, mounted on
  // `GlobalTutorialBar` — an onboarding-owned surface that renders on every
  // page. See that file for what consumes the flag and what keeps it.

  // M-2 Effect B — Day-scoped realtime channels (runs when date OR shop changes)
  // Creates/tears down only the channel layer. No DB queries here — so the
  // teardown/recreate on date-switch is fast with no window of missed events.
  //
  // RTC-A2: collapse the four pre-A2 page-level channels
  //   (`dashboard-tickets-...`, `dashboard-technicians-...`,
  //    `dashboard-tech-users-...`, `dashboard-timeclock-...`)
  // into ONE consolidated channel with four `.on('postgres_changes', ...)`
  // listener registrations. Pre-A2 each Dashboard mount opened FOUR
  // websocket subscriptions for the same shop scope and the same
  // useEffect lifecycle — every event therefore traversed four
  // independent channel objects. Post-A2 the same four listener bodies
  // are registered against ONE channel; behavior is preserved verbatim
  // because Supabase JS dispatches each `.on('postgres_changes', ...)`
  // listener independently regardless of how many channels host it.
  // The listener bodies below are byte-identical to the pre-A2 versions
  // (including all R37 / FIX-R34 / FIX-R35 / FIX-R36 / CORE-02 / FIX-
  // R34-CRIT-1 / FIX-R34-CRIT-2 markers); only the channel-creation +
  // `.subscribe(...)` + `removeChannel(...)` boilerplate was
  // consolidated 4→1. The status callback is also consolidated: pre-
  // A2 each of the four `.subscribe(...)` calls had its own
  // `(status, err) => console.error('[Dashboard] X realtime channel
  // status: ...')` log line; post-A2 a SINGLE callback covers all four
  // listeners. The pre-A2 per-listener log identifiers are preserved
  // in the doctrinal block below (FIX-R35-MED-1 + 3× FIX-R36-LOW-1)
  // so a future regression that re-introduces per-channel `.subscribe`
  // can re-anchor on the original markers.
  //
  // OUT OF SCOPE — explicitly NOT merged in this round:
  //   - `useLiveReshuffle` (different filter axis: `scheduled_date`,
  //     not `shop_id`; date-driven cascade lifecycle is intentionally
  //     separate). Runs on its own channel `schedule-changes-...`.
  //   - `useReminderTriggers` / `ReminderTriggerProvider`
  //     (App.tsx-mounted around the page, not page-mounted; reminder
  //     dedup state survives navigation). Runs on its own channel
  //     `reminders-shop-...`.
  //   - `useWeatherAlerts` (separate `weather_alerts` table, separate
  //     hook lifecycle).
  //   - all auth-shell channels (AuthContext memberships,
  //     SupportModeContext support_sessions, useRealtimeStatus
  //     heartbeat, TopBar timeclock-profile / notifications).
  //
  // Pre-A2 channel names (preserved here as a doctrinal anchor for
  // the closure test §A — the test scans for them as REMOVED):
  //   `dashboard-tickets-${shop_id}-${selectedDayLabel}`
  //   `dashboard-technicians-${shop_id}`
  //   `dashboard-tech-users-${shop_id}`
  //   `dashboard-timeclock-${shop_id}`
  //
  // Post-A2 channel name:
  //   `dashboard-realtime-${shop_id}-${selectedDayLabel}`
  // The date suffix is preserved (matches the pre-A2 tickets-channel
  // shape) so two browser tabs on the same shop but different dates
  // get distinct channel names. The other three pre-A2 channels did
  // NOT include date in their name; effectively the new merged
  // channel inherits the tickets-channel naming axis. This does NOT
  // change lifecycle: the pre-A2 useEffect already had
  // `selectedDayLabel` in its deps array, so all four channels were
  // already torn-down + recreated on date change.
  useEffect(() => {
    if (!activeShop?.id) return;

    // Subscribe to real-time updates with immediate refresh.
    // FIX-R34-CRIT-1: server-side filter promoted from date → shop_id.
    // Supabase postgres_changes supports only a single filter column;
    // shop_id is the tenant boundary and must be enforced server-side.
    // Date remains a client-side guard below so we still ignore
    // cross-day noise. (RTC-A2: filter retained verbatim.)
    const dashboardChannel = supabase
      .channel(`dashboard-realtime-${activeShop?.id}-${selectedDayLabel}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tickets',
        filter: `shop_id=eq.${activeShop?.id}`,
      }, async (payload) => {
        console.log('Ticket change detected:', payload);

        // Defense-in-depth guards for shop/date (shop guard is now also server-side).
        const shopId = activeShop?.id;
        if (!shopId) return;

        if (payload.eventType === 'INSERT') {
          const row: any = payload.new;
          if (row?.shop_id !== shopId || row?.scheduled_date !== selectedDayLabel) return;
        }

        // For UPDATE: do NOT filter on `payload.new.scheduled_date` here.
        // Doing so silently dropped events where a ticket's scheduled_date
        // moved AWAY from the current Dayboard (e.g. user changed a ticket
        // from today to tomorrow). The event was discarded, the local
        // `tickets` state kept the stale row, the `ticketsForSelectedDate`
        // memo at L1502-1507 passed it through (because the local copy
        // still had scheduled_date===today), and the ghost card stayed on
        // today's Dayboard until a date toggle forced a fresh fetch. The
        // post-fetch logic below now decides move-in vs move-out vs
        // in-place vs no-op from the FETCHED row's actual scheduled_date,
        // which doesn't rely on REPLICA IDENTITY FULL being set on
        // `tickets`. Only the shop_id guard is needed at this gate.
        if (payload.eventType === 'UPDATE') {
          const row: any = payload.new;
          if (row?.shop_id !== shopId) return;
        }

        if (payload.eventType === 'DELETE') {
          const row: any = payload.old;
          if (row?.shop_id !== shopId || row?.scheduled_date !== selectedDayLabel) return;
        }

        // Immediately update local state based on the change
        if (payload.eventType === 'UPDATE') {
          // CORE-02 FIX: Skip updates to actively dragged ticket to prevent mid-drag UI disruption
          if (isDraggingRef.current && payload.new.id === activeTicketRef.current?.id) {
            console.log('[REALTIME] Skipping update for actively dragged ticket:', payload.new.id);
            return;
          }

          // R37: Stamp a monotonic version BEFORE the fetch so an
          // out-of-order resolution can be discarded.
          const ticketId = payload.new.id as string;
          const myVersion = (realtimeFetchVersions.current.get(ticketId) || 0) + 1;
          realtimeFetchVersions.current.set(ticketId, myVersion);

          // Fetch the full ticket with relations to ensure jobs are included
          const { data: updatedTicket, error: realtimeFetchErr } = await supabase
            .from('tickets')
            .select(`*, repair_number, vehicle:vehicles(id, year, make, model, engine_size, license_plate, plate_jurisdiction), ticket_jobs(minutes_override, notes, separate_time, diagnostic_completed_at, actual_minutes, job_status, job:jobs(*)), technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end), completed_technician:technicians!completed_by_tech_id(id, name)`)
            .eq('id', payload.new.id)
            .single();

          if (realtimeFetchErr) {
            logDashboardSupabaseError('realtime_single_ticket', realtimeFetchErr);
          }

          // R37: Drop the result if a newer fetch has been stamped for the
          // same ticket — that fetch's setState carries fresher truth.
          if (realtimeFetchVersions.current.get(ticketId) !== myVersion) {
            console.log('[REALTIME] Discarding stale fetch for ticket', ticketId, '(superseded by newer event)');
            return;
          }

          if (updatedTicket) {
            console.log('[REALTIME] Updating ticket:', updatedTicket.id, 'status:', updatedTicket.status, 'pause_reason:', updatedTicket.pause_reason);

            // Decide move-in / move-out / in-place / no-op from the FETCHED
            // row's actual scheduled_date (post-update, post-RLS). This
            // replaces the pre-fix `prev.map(...)`-only path that silently
            // kept ghost cards on the Dayboard when a ticket's date was
            // changed away from selectedDayLabel.
            const ticketIsForThisDay =
              (updatedTicket as any).shop_id === shopId &&
              ((updatedTicket as any).scheduled_date === selectedDayLabel
                || isEarlierDayRunningOnToday(updatedTicket as any));  // NOV3 (A3)
            const updatedTicketId = payload.new.id as string;

            const transformedTicket = {
              ...updatedTicket,
              jobs: updatedTicket.ticket_jobs?.map((tj: any) => ({
                ...tj.job,
                standardMinutes: tj.minutes_override || tj.job.standard_minutes,
                notes: tj.notes,
                // Match the canonical job-shape used by the date-scoped
                // fetch path. See initializeData transformation for the
                // same fix and rationale.
                separateTime: tj.separate_time || false,
                diagnosticCompletedAt: tj.diagnostic_completed_at,
                actualMinutes: tj.actual_minutes,
                jobStatus: tj.job_status,
              })) || [],
              vehicle: dayboardCardVehicle(updatedTicket.vehicle, { showPlate: showPlateRef.current }),
              vehiclePresent: isVehiclePresent(updatedTicket),
              predictedMinutes: updatedTicket.predicted_minutes,
              assignedTechId: updatedTicket.assigned_tech_id,
              pauseReason: updatedTicket.pause_reason,
              startTime: formatShopTime(updatedTicket.start_time, shopTimezone, 'short'),
              completed_by_name: (updatedTicket as any).completed_technician?.name || null,
            };

            setTickets(prev => {
              const exists = prev.some(t => t.id === updatedTicketId);

              if (ticketIsForThisDay && exists) {
                // In-place update — pre-fix behavior, the common case.
                return prev.map(t => (t.id === updatedTicketId ? transformedTicket : t));
              }
              if (ticketIsForThisDay && !exists) {
                // Move-in: ticket's scheduled_date just became
                // selectedDayLabel from a different value, or it was
                // missing from local state for any other reason. Append.
                console.log('[REALTIME] Ticket moved into selected day, adding:', updatedTicketId);
                return [...prev, transformedTicket];
              }
              if (!ticketIsForThisDay && exists) {
                // Move-out: ticket's scheduled_date changed away from
                // selectedDayLabel (or shop_id changed). Remove from
                // local state so the card disappears without forcing
                // the user to date-toggle to trigger a refetch.
                console.log('[REALTIME] Ticket moved out of selected day, removing:', updatedTicketId);
                return prev.filter(t => t.id !== updatedTicketId);
              }
              // No-op: ticket isn't for this Dayboard and isn't in
              // local state — UPDATE event was for a different day's
              // ticket; nothing to do.
              return prev;
            });
          }
        } else if (payload.eventType === 'INSERT') {
          // R37: Same per-ticket version stamp as the UPDATE branch so an
          // INSERT followed quickly by an UPDATE on the same ticket can't
          // race the INSERT into a stale terminal setState.
          const ticketId = payload.new.id as string;
          const myVersion = (realtimeFetchVersions.current.get(ticketId) || 0) + 1;
          realtimeFetchVersions.current.set(ticketId, myVersion);

          // Fetch the full ticket with relations
          const { data: newTicket, error: realtimeInsertErr } = await supabase
            .from('tickets')
            .select(`*, repair_number, vehicle:vehicles(id, year, make, model, engine_size, license_plate, plate_jurisdiction), ticket_jobs(minutes_override, notes, separate_time, diagnostic_completed_at, actual_minutes, job_status, job:jobs(*)), technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end), completed_technician:technicians!completed_by_tech_id(id, name)`)
            .eq('id', payload.new.id)
            .single();

          if (realtimeInsertErr) {
            logDashboardSupabaseError('realtime_insert_ticket', realtimeInsertErr);
          }

          if (realtimeFetchVersions.current.get(ticketId) !== myVersion) {
            console.log('[REALTIME] Discarding stale INSERT fetch for ticket', ticketId, '(superseded by newer event)');
            return;
          }

          if (newTicket) {
            // Only add if not already in state (prevents duplicates from multiple INSERT events)
            setTickets(prev => {
              if (prev.some(t => t.id === newTicket.id)) {
                return prev;
              }
              return [...prev, {
                ...newTicket,
                jobs: newTicket.ticket_jobs?.map((tj: any) => ({
                  ...tj.job,
                  standardMinutes: tj.minutes_override || tj.job.standard_minutes,
                  notes: tj.notes,
                  // Match the canonical job-shape used by the date-scoped
                  // fetch path. See initializeData transformation for
                  // the same fix and rationale.
                  separateTime: tj.separate_time || false,
                  diagnosticCompletedAt: tj.diagnostic_completed_at,
                  actualMinutes: tj.actual_minutes,
                  jobStatus: tj.job_status,
                })) || [],
                vehicle: dayboardCardVehicle(newTicket.vehicle, { showPlate: showPlateRef.current }),
                vehiclePresent: isVehiclePresent(newTicket),
                predictedMinutes: newTicket.predicted_minutes,
                assignedTechId: newTicket.assigned_tech_id,
                pauseReason: newTicket.pause_reason,
                startTime: formatShopTime(newTicket.start_time, shopTimezone, 'short'),
                completed_by_name: (newTicket as any).completed_technician?.name || null,
              }];
            });

            // OptiQueue online booking alert
            if (newTicket.source === 'optiqueue') {
              const vehicleInfo = newTicket.vehicle
                ? `${newTicket.vehicle.year} ${newTicket.vehicle.make} ${newTicket.vehicle.model}`
                : 'Unknown vehicle';

              toast({
                title: '🌐 New Online Appointment',
                description: `${newTicket.customer_name || 'Customer'} booked ${vehicleInfo}`,
                duration: 8000,
              });

              // Add to flashing set
              setNewOptiQueueTicketIds(prev => new Set(prev).add(newTicket.id));

              // Remove from flashing set after animation completes (~4 seconds)
              setTimeout(() => {
                setNewOptiQueueTicketIds(prev => {
                  const next = new Set(prev);
                  next.delete(newTicket.id);
                  return next;
                });
              }, 4000);
            }
          }
        } else if (payload.eventType === 'DELETE') {
          setTickets(prev => prev.filter(t => t.id !== payload.old.id));
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'technicians', filter: `shop_id=eq.${activeShop?.id}` }, async (payload) => {
        console.log('Technician change detected:', payload);

        // Immediately update local state
        if (payload.eventType === 'UPDATE') {
          // Fetch full technician data with user_id
            const { data: updatedTech } = await supabase
              .from('technicians')
              .select('*, technician_users(user_id)')
              .eq('id', payload.new.id)
            .single();

          if (updatedTech) {
            setTechnicians(prev => prev.map(t =>
              t.id === payload.new.id ? {
                ...updatedTech,
                user_id: updatedTech.technician_users?.[0]?.user_id || null,
              } : t
            ));
          }
        } else if (payload.eventType === 'INSERT') {
            const { data: newTech } = await supabase
              .from('technicians')
              .select('*, technician_users(user_id)')
              .eq('id', payload.new.id)
            .single();

          if (newTech) {
            setTechnicians(prev => [...prev, {
              ...newTech,
              user_id: newTech.technician_users?.[0]?.user_id || null,
            }]);
          }
        } else if (payload.eventType === 'DELETE') {
          setTechnicians(prev => prev.filter(t => t.id !== payload.old.id));
        }
      })
      // Subscribe to technician_users changes to update user_id mappings.
      // FIX-R34-CRIT-2: the previous comment asserted that technician_users has
      // no server-filterable shop_id — the schema disagrees (types.ts:13378).
      // The table is a junction with (user_id, technician_id, shop_id); scope
      // the subscription by shop_id so cross-tenant link changes no longer
      // stream to this browser. Client-side guards below remain as defense-in-depth.
      // (RTC-A2: filter + body retained verbatim.)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'technician_users',
        filter: `shop_id=eq.${activeShop?.id}`,
      }, (payload) => {
        console.log('Technician-User link change detected:', payload);
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const newMapping = payload.new as any;
          if (newMapping?.shop_id !== activeShop?.id) return;
          setTechnicians(prev => {
            if (!prev.some(t => t.id === newMapping.technician_id)) return prev;
            return prev.map(t =>
              t.id === newMapping.technician_id ? { ...t, user_id: newMapping.user_id } : t
            );
          });
        } else if (payload.eventType === 'DELETE') {
          const oldMapping = payload.old as any;
          if (oldMapping?.shop_id && oldMapping.shop_id !== activeShop?.id) return;
          setTechnicians(prev => prev.map(t =>
            t.id === oldMapping.technician_id ? { ...t, user_id: null } : t
          ));
        }
      })
      .subscribe((status, err) => {
        // RTC-A2: consolidated status callback covers all four
        // listeners (tickets / technicians / technician_users /
        // time_clock_entries). Pre-A2 each of the four channels had
        // its own `(status, err) => console.error(...)` callback —
        // FIX-R35-MED-1 (tickets) + FIX-R36-LOW-1 (×3 for the other
        // three). Post-A2 a single callback surfaces drops for the
        // entire merged channel; the listener-binding error is
        // carried in `err`. All four pre-A2 doctrinal markers are
        // preserved in the doctrinal block above so future
        // regression detection can re-anchor.
        // Batch 11: normalized status logging. Pre-bump, EVERY CLOSED
        // (including the intentional teardown that supabase-js fires
        // on shop/date change → `removeChannel(...)` in the effect
        // cleanup at L1063) dispatched a `ticket-updated` self-heal
        // refetch. That created a feedback loop:
        //   1. Date change → effect cleanup → removeChannel().
        //   2. supabase-js fires CLOSED with no err.
        //   3. Pre-bump branch fired dispatchEvent('ticket-updated').
        //   4. The date-switch refetch listener (L1102) re-ran with
        //      the OLD shop/date scope on a now-dead channel.
        //   5. Console flooded with `[Dashboard] realtime channel
        //      status: CLOSED` red lines per page lifecycle event.
        // Post-bump: `isAbnormalRealtimeClose` returns true only for
        // CHANNEL_ERROR, TIMED_OUT, or CLOSED-with-err — i.e. genuine
        // websocket failures the operator needs the self-heal for.
        // Intentional CLOSED-on-cleanup logs at debug and does NOT
        // dispatch the refetch event.
        logRealtimeStatus('[Dashboard]', status, err);
        setLiveState((prev) => nextLiveState(prev, String(status), isAbnormalRealtimeClose(status, err), new Date()));
        if (String(status) === 'SUBSCRIBED' && liveLostRef.current) {
          liveLostRef.current = false;
          console.warn('[REALTIME] channel rejoined after a loss, re-reading the Dayboard');
          try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort; ignore */ }
          // R3-A Option B: changes the server made while we were deaf are re-read after it reconciles now.
          reconcileNowRef.current();
        }
        if (isAbnormalRealtimeClose(status, err)) {
          liveLostRef.current = true;
          console.warn('[REALTIME] channel unhealthy, forcing Dayboard refetch');
          try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort; ignore */ }
        }
      });

    // Core-100 R4 X4 (CORE-AD Rel, "reflect state promptly"): the joined-table listeners live on their OWN channel.
    // time_clock_entries / ticket_jobs / vehicles / customers are not in the supabase_realtime publication, and the
    // Realtime server refuses a join whose postgres_changes bindings include an unpublished table ("Unable to
    // subscribe to changes with given parameters") — for EVERY binding of that join. On one merged channel that
    // silently silenced the tickets / technicians / technician_users listeners too: the channel said SUBSCRIBED and
    // the board never heard another device's change. Kept separate, a refused joined-table channel cannot take the
    // board's own live updates (and the stale-board banner they drive) down with it.
    const dashboardJoinedChannel = supabase
      .channel(`dashboard-joined-tables-${activeShop?.id}-${selectedDayLabel}`)
      // Shared timeclock subscription — replaces per-tech presence channels AND StaffPresenceIndicator listener
      .on('postgres_changes', { event: '*', schema: 'public', table: 'time_clock_entries', filter: `shop_id=eq.${activeShop.id}` }, () => {
        // Invalidate technician presence — TechnicianColumn will re-render via state change
        setTechnicians(prev => [...prev]); // Force re-render for presence updates
        // Signal StaffPresenceIndicator to refresh via refreshKey prop
        setStaffRefreshKey(prev => prev + 1);
        setTimeClockRefreshKey(prev => prev + 1);
      })
      // ============================================================
      // Joined-table coverage: a `tickets` row UPDATE only fires when
      // the ticket itself is written. Edits to joined tables that
      // affect visible card data (vehicle year/make/model, customer
      // name/phone, job duration / status / diagnostic-complete state)
      // do not write the parent ticket and therefore did not deliver
      // a realtime UPDATE — Device A's edit was invisible to Device B
      // until a manual date toggle. These listeners trigger the
      // existing date-scoped refetch fallback via `ticket-updated`
      // rather than patching joined rows by hand; the refetch already
      // joins all four child tables, so the local state converges.
      // All three child tables have a `shop_id` column (verified via
      // `src/integrations/supabase/types.ts`), so the shop scoping
      // tracks the parent channel correctly.
      // ============================================================
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ticket_jobs', filter: `shop_id=eq.${activeShop?.id}` }, () => {
        console.log('[REALTIME] ticket_jobs changed, refetching Dayboard');
        try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort; ignore */ }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles', filter: `shop_id=eq.${activeShop?.id}` }, () => {
        console.log('[REALTIME] vehicles changed, refetching Dayboard');
        try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort; ignore */ }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customers', filter: `shop_id=eq.${activeShop?.id}` }, () => {
        console.log('[REALTIME] customers changed, refetching Dayboard');
        try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort; ignore */ }
      })
      .subscribe((joinedStatus, joinedErr) => logRealtimeStatus('[Dashboard joined tables]', joinedStatus, joinedErr));

    return () => {
      supabase.removeChannel(dashboardChannel);
      supabase.removeChannel(dashboardJoinedChannel);
    };
  }, [selectedDayLabel, activeShop?.id, shopTimezone]);

  // Fallback refetch trigger. Any mutation flow that wants to nudge the
  // Dayboard without going through realtime (AutoBump's onTicketUpdate,
  // EODBufferToast's Convert, EODRolloverModal save, optimizer suggestions,
  // ticketTimerManager rollover completion, etc.) dispatches
  // `window.dispatchEvent(new CustomEvent('ticket-updated'))`. Pre-fix,
  // there was no listener for that event anywhere in `src/**`, so each
  // dispatch was a silent no-op and the Dayboard relied entirely on the
  // postgres_changes realtime channel. When realtime is delayed, closed
  // during a token-refresh recovery, or misses a cross-day move payload,
  // the operator had to manually toggle the date picker to force a
  // refetch. The tick counter below makes the existing date-scoped
  // refetch effect re-run whenever `ticket-updated` fires — a single
  // small fallback that pairs with the existing realtime path.
  const [refetchTicketsTick, setRefetchTicketsTick] = useState(0);
  useEffect(() => {
    const handler = () => setRefetchTicketsTick(t => t + 1);
    window.addEventListener('ticket-updated', handler);
    return () => window.removeEventListener('ticket-updated', handler);
  }, []);

  // Cross-device Dayboard invalidation channel setup. The channel,
  // receiver listener, debouncing, and event lifecycle all live in
  // src/utils/dayboardInvalidation.ts — a single module that any
  // mutation flow can call into without prop-drilling or context.
  // Mount the channel on shop change; dispose on unmount or shop change.
  useEffect(() => {
    if (!activeShop?.id) return;
    // Shared with the FOH queue badge (reference-counted), so leaving the
    // Dashboard does not deafen the badge, and vice versa.
    return retainDayboardBroadcastChannel(activeShop.id);
  }, [activeShop?.id]);

  // Tab-return reconciliation. When the tab is backgrounded for an
  // extended period, the browser may suspend the Supabase Realtime
  // websocket (Chrome intensive throttling, Safari WebSocket suspension).
  // Phoenix does NOT redeliver events fired during the suspended window
  // on reconnect — those events are simply lost. AutoBump's own
  // `visibilitychange` listener kicks an immediate tick on return, but
  // the Dayboard itself had no equivalent: it relied on the next normal
  // mutation or date toggle to refetch. Firing one `ticket-updated`
  // dispatch on visibility return triggers the existing fallback refetch
  // (via the listener above) and closes the suspended-websocket gap.
  // Idempotent — if no events were missed, the refetch just re-reads
  // the same data the local state already has.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        window.dispatchEvent(new CustomEvent('ticket-updated'));
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []);

  // CORE-02 FIX: Refetch tickets when selected date changes
  // This ensures tickets created while viewing another date appear when navigating back
  useEffect(() => {
    if (!activeShop?.id) return;

    const fetchTicketsForSelectedDate = async () => {
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .select(`
          *, repair_number,
          vehicle:vehicles(id, year, make, model, engine_size, license_plate, plate_jurisdiction),
          ticket_jobs(minutes_override, notes, separate_time, diagnostic_completed_at, actual_minutes, job_status, job:jobs(*)),
          technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end),
          completed_technician:technicians!completed_by_tech_id(id, name),
          scheduled_technician:technicians!tickets_scheduled_tech_id_fkey(id, name)
        `)
        .eq('shop_id', activeShop.id)
        // DATE-SCOPED FETCH. NOV3 (A3): on TODAY's board, work still RUNNING on an earlier day is shown too — the
        // technician must always be able to pause or stop it (Floyd's: yesterday's TKT-0153 ran all day on Nov 3 but
        // only today's tickets were loaded, so its controls were on no screen).
        .or(selectedDayLabel === getTodayInShopTz(shopTimezone)
          ? `scheduled_date.eq.${selectedDayLabel},and(timer_status.eq.running,scheduled_date.lt.${selectedDayLabel})`
          : `scheduled_date.eq.${selectedDayLabel}`);

      if (ticketError) {
        logDashboardSupabaseError('date_switch_refetch', ticketError, {
          selectedDate: selectedDayLabel,
        });
        return;
      }

      if (ticketData) {
        // Fetch parking spots for label lookup
        const { data: spots } = await supabase
          .from('parking_lot_spots')
          .select('id, label')
          .eq('shop_id', activeShop.id);

        // Reuse the same transformation pattern as initializeData
        const transformedTickets = ticketData.map(t => {
          const spot = spots?.find(s => s.id === t.parking_spot_id);
          return {
            ...t,
            jobs: t.ticket_jobs?.map((tj: any) => ({
              ...tj.job,
              standardMinutes: tj.minutes_override || tj.job.standard_minutes,
              notes: tj.notes,
              separateTime: tj.separate_time || false,
              diagnosticCompletedAt: tj.diagnostic_completed_at,
              actualMinutes: tj.actual_minutes,
              jobStatus: tj.job_status,
            })) || [],
            vehicle: dayboardCardVehicle(t.vehicle, { showPlate: showPlateRef.current }),
            vehiclePresent: isVehiclePresent(t),
            predictedMinutes: t.predicted_minutes,
            assignedTechId: t.assigned_tech_id,
            customerName: t.customer_name,
            customerPhone: t.customer_phone,
            pauseReason: t.pause_reason,
            startTime: formatShopTime(t.start_time, shopTimezone, 'short'),
            completed_by_name: (t as any).completed_technician?.name || null,
            parking_spot: spot ? { id: spot.id, label: spot.label } : null,
          };
        });
        
        console.log(`[DATE-SWITCH] Fetched ${transformedTickets.length} tickets for ${selectedDayLabel}`);
        
        // Bug 3.2 fix: Replace tickets entirely on date switch (no merge)
        setTickets(transformedTickets);
      }
    };

    fetchTicketsForSelectedDate();
  // L-4: shopTimezone used for formatShopTime inside this effect — must be in deps
  // refetchTicketsTick: bumps when any `ticket-updated` event fires, so this
  //                     effect re-runs as a fallback when realtime missed an
  //                     update (or is closed during recovery).
  }, [activeShop?.id, selectedDayLabel, shopTimezone, refetchTicketsTick]);

  // LOW 2.4 + LOW 2.5: Reset drag state and pending move on date change
  useEffect(() => {
    setActiveTicket(null);
    setIsDragging(false);
    setDragOverTechId(null);
    setPreviewStartTime(null);
    setHoverIndex(-1);
    setPendingMoveTicketId(null);
    setOriginalTicketData(null);
  }, [selectedDayLabel]);

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id as string;
    
    // Check if dragging a technician
    const tech = technicians.find(t => t.id === activeId);
    if (tech) {
      setActiveTechnician(tech);
      setIsDragging(true);
      return;
    }
    
    // Otherwise dragging a ticket
    const ticket = tickets.find(t => t.id === activeId);
    setActiveTicket(ticket);
    setIsDragging(true);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    
    // Handle technician drag over - show cascade preview
    if (activeTechnician && over) {
      // Extract the actual technician ID from the drop zone
      const overTechId = over.data?.current?.technicianId || over.id.toString().replace('tech-drop-', '');
      
      // MED 1.2: Use shared sorting helper instead of inline IIFE
      const sortedTechs = getSortedTechnicians(
        technicians,
        selectedDate,
        technicianOrder,
        null,
        selectedDayLabel
      );
      
      const overIndex = sortedTechs.findIndex(t => t.id === overTechId);
      setTechHoverIndex(overIndex);
      return;
    }
    
    setTechHoverIndex(-1);
    
    if (!over || !activeTicket) {
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      return;
    }

    // Determine the target technician
    let newTechId: string;
    const overTicket = tickets.find(t => t.id === over.id);
    if (overTicket) {
      newTechId = overTicket.assigned_tech_id;
    } else {
      newTechId = over.id as string;
    }

    setDragOverTechId(newTechId);

    const tech = technicians.find(t => t.id === newTechId);
    if (!tech) return;

    const techTickets = ticketsForSelectedDate
      .filter(t => t.assigned_tech_id === newTechId && t.status !== 'Completed' && t.id !== activeTicket.id)
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

    let insertIndex = techTickets.length; // Default to end
    let newStartIso: string;

    if (overTicket && overTicket.assigned_tech_id === newTechId) {
      // Hovering over a specific ticket - insert before it
      insertIndex = techTickets.findIndex(t => t.id === overTicket.id);
      if (insertIndex === -1) insertIndex = techTickets.length;
      
      if (insertIndex === 0) {
        // Insert at beginning - use shift start (shop TZ aware).
        // CSI-S3: canonical per-day hours via getTechDayHours(). Falls
        // back to the deprecated flat columns only when the per-day
        // hours entry is empty — same dayHours-then-flat-then-default
        // pattern as CSI-S2 sites (technicianCapacity, rolloverChain
        // segment-anchor, invariants SPECIALTY_TECH_SHIFT).
        const hours = getTechDayHours(tech, format(selectedDate, 'yyyy-MM-dd'))
          || { open: tech.shift_start, close: tech.shift_end };
        const shiftTimeStr = (hours.open || tech.shift_start || '08:00').slice(0, 5);
        newStartIso = getTodaySafeStartTime(shiftTimeStr, selectedDayLabel, shopTimezone);
      } else {
        // Insert after previous ticket
        const prevTicket = techTickets[insertIndex - 1];
        const prevStart = new Date(prevTicket.start_time);
        const prevOptiTime = calculateWallMinutes(
          prevTicket.predictedMinutes || 0,
          tech.efficiency_percentage || 100,
          resolvePlanningBufferPct(tech.buffer_percentage)
        );
        newStartIso = new Date(prevStart.getTime() + prevOptiTime * 60000 + 60000).toISOString();
      }
    } else {
      // Hovering over empty space or column - insert at end
      if (techTickets.length > 0) {
        const lastTicket = techTickets[techTickets.length - 1];
        const lastStart = new Date(lastTicket.start_time);
        const optiTime = calculateWallMinutes(
          lastTicket.predictedMinutes || 0,
          tech.efficiency_percentage || 100,
          resolvePlanningBufferPct(tech.buffer_percentage)
        );
        newStartIso = new Date(lastStart.getTime() + optiTime * 60000 + 60000).toISOString();
      } else {
        // No tickets - use shift start (shop TZ aware).
        // CSI-S3: canonical per-day hours via getTechDayHours(); same
        // canonical-then-flat-then-default pattern as the empty-column-
        // insert site above and the CSI-S2 scheduling consumers.
        const hours = getTechDayHours(tech, format(selectedDate, 'yyyy-MM-dd'))
          || { open: tech.shift_start, close: tech.shift_end };
        const shiftTimeStr = (hours.open || tech.shift_start || '08:00').slice(0, 5);
        newStartIso = getTodaySafeStartTime(shiftTimeStr, selectedDayLabel, shopTimezone);
      }
    }

    // M-2: Apply break-aware adjustment so preview matches server placement
    // R5-L-1: Filter non-recurring breaks by selectedDayLabel to match server logic
    const techBreaks = [
      ...(tech.recurring_breaks || []),
      ...(tech.non_recurring_breaks || []).filter(
        (b: any) => b.date === selectedDayLabel
      ),
    ];
    if (techBreaks.length > 0) {
      const adjustedStart = adjustForBreaks(new Date(newStartIso), techBreaks, selectedDayLabel, shopTimezone);
      newStartIso = adjustedStart.toISOString();
    }

    setHoverIndex(insertIndex);
    setPreviewStartTime(newStartIso);
  };

  // WS-ABSENT (constitution closure 2026-10-20; CORE-AB:43, CORE-AD:80, ledger FD-S3): marking a technician absent
  // used to commit smart-assign's #1 for every one of their tickets right here ('absent-tech-reassign', recorded
  // automatic) and park what it could not place — no person chose, and no shop setting enables that path. Now it only
  // opens the review: smart-assign's ranking is shown there, and each move is the person's confirmed choice.
  const handleReassignNeeded = (ticketsToReassign: any[], technicianName: string) => {
    if (!activeShop?.id) return;
    setAbsentReview({ tickets: ticketsToReassign, technicianName });
  };

  // REMOVED: handleDeleteTicket — dead code (hard-delete without descendant cleanup).
  // Live delete path is TicketKebabMenu:handleDelete, which calls the atomic
  // `delete_ticket_family_if_quiescent` authority. 8G.3A.1 retired the
  // `cancelTicketChain` half this comment used to name.

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Handle technician reorder
    if (activeTechnician && over) {
      // Extract the actual technician ID from the drop zone
      const overTechId = over.data?.current?.technicianId || over.id.toString().replace('tech-drop-', '');
      
      // MED 1.2: Use shared sorting helper
      const orderedTechs = getSortedTechnicians(
        technicians,
        selectedDate,
        technicianOrder,
        null,
        selectedDayLabel
      );
      
      const activeIndex = orderedTechs.findIndex(t => t.id === active.id);
      const overIndex = orderedTechs.findIndex(t => t.id === overTechId);
      
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const reordered = [...orderedTechs];
        const [removed] = reordered.splice(activeIndex, 1);
        reordered.splice(overIndex, 0, removed);
        
        const newOrder = reordered.map(t => t.id);
        setTechnicianOrder(newOrder);
        // Bug 3.1 fix: Use same shopId resolver for write
        const shopId = activeShop?.id || membership?.shop_id;
        if (shopId) {
          localStorage.setItem(`technician-order-${shopId}`, JSON.stringify(newOrder));
        }
        
        toast({
          title: 'Technician Order Updated',
          description: 'Column arrangement has been saved',
        });
      }
      
      setActiveTechnician(null);
      setTechHoverIndex(-1);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      setIsDragging(false);
      return;
    }
    
    // Permission guard: block drag-drop for non-authorized roles
    if (!canDragDropTickets) {
      setActiveTicket(null);
      setIsDragging(false);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      return;
    }

    if (!activeTicket || !over) {
      setActiveTicket(null);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      setIsDragging(false);
      return;
    }

    const ticketId = active.id;
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;

    // BUG-3.5 fix: Guard against moving completed or actively-running tickets
    // Paused tickets (even with actually_started=true) ARE allowed to be moved
    if (ticket.status === 'Completed' || ticket.timer_status === 'running') {
      toast({
        title: 'Cannot move active/completed work',
        description: 'This ticket is already in progress or completed.',
        variant: 'destructive',
      });
      setActiveTicket(null);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      setIsDragging(false);
      return;
    }

    // Determine target technician and calculate desired start time (as UTC ISO string)
    let newTechId: string;
    let desiredStartTime: string | null = null;

    const overTicket = tickets.find(t => t.id === over.id);
    if (overTicket) {
      // When the hovered ticket is in the "parked but scheduled for X" state
      // (assigned_tech_id=null, scheduled_tech_id=X, parking_lot=true — the
      // fallback introduced by migration 20260601160000), assigned_tech_id
      // is null. Without this fallback, newTechId would become null and the
      // reassign-ticket Edge Function would reject with a 400
      // `"targetTechId is required when not moving to parking lot"` —
      // surfaced to the user as the generic supabase-js
      // "Edge Function returned a non-2xx status code" toast.
      newTechId =
        (overTicket as any).assigned_tech_id ??
        (overTicket as any).scheduled_tech_id ??
        (over.id as string);
    } else {
      newTechId = over.id as string;
    }

    const oldTechId = ticket.assigned_tech_id;
    const tech = technicians.find(t => t.id === newTechId);
    if (!tech) return;

    // Same-column chain math must include parked-but-scheduled tickets,
    // because the widened TechnicianColumn filter renders them in this
    // tech's column (matching scheduled_tech_id). Skip our own row and
    // anything Completed.
    const techTickets = ticketsForSelectedDate
      .filter(t => {
        if (t.id === ticket.id) return false;
        if (t.status === 'Completed') return false;
        const ownsNow = t.assigned_tech_id === newTechId;
        const parkedScheduledHere =
          (t as any).parking_lot === true &&
          (t as any).scheduled_tech_id === newTechId &&
          t.assigned_tech_id == null;
        return ownsNow || parkedScheduledHere;
      })
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

    let insertIndex = techTickets.length; // Default to end

    if (overTicket && overTicket.assigned_tech_id === newTechId) {
      // Dropping on a specific ticket - insert before it
      insertIndex = techTickets.findIndex(t => t.id === overTicket.id);
      if (insertIndex === -1) insertIndex = techTickets.length;
      
      if (insertIndex === 0) {
        // HIGH 1.1: Insert at beginning - use shift start via shopLocalToUtc (not browser Date).
        // CSI-S3: canonical per-day hours via getTechDayHours(); same
        // canonical-then-flat-then-default pattern as the sibling
        // insert-at-beginning site in handleDragEnd above.
        const hours = getTechDayHours(tech, format(selectedDate, 'yyyy-MM-dd'))
          || { open: tech.shift_start, close: tech.shift_end };
        const shiftTimeStr = (hours.open || tech.shift_start || '08:00').slice(0, 5);
        desiredStartTime = getTodaySafeStartTime(shiftTimeStr, selectedDayLabel, shopTimezone);
      } else {
        // Insert after previous ticket — prevTicket.start_time is already UTC
        const prevTicket = techTickets[insertIndex - 1];
        const prevStart = new Date(prevTicket.start_time);
        const prevOptiTime = calculateWallMinutes(
          prevTicket.predictedMinutes || 0,
          tech.efficiency_percentage || 100,
          resolvePlanningBufferPct(tech.buffer_percentage)
        );
        desiredStartTime = new Date(prevStart.getTime() + prevOptiTime * 60000 + 60000).toISOString();
      }
    } else {
      // Dropping on empty space or column - insert at end
      if (techTickets.length > 0) {
        const lastTicket = techTickets[techTickets.length - 1];
        const lastStart = new Date(lastTicket.start_time);
        const optiTime = calculateWallMinutes(
          lastTicket.predictedMinutes || 0,
          tech.efficiency_percentage || 100,
          resolvePlanningBufferPct(tech.buffer_percentage)
        );
        desiredStartTime = new Date(lastStart.getTime() + optiTime * 60000 + 60000).toISOString();
      } else {
        // HIGH 1.1: No tickets - use shift start via shopLocalToUtc (not browser Date).
        // CSI-S3: canonical per-day hours via getTechDayHours(); same
        // canonical-then-flat-then-default pattern as the 3 sibling
        // shift-start derivation sites in this file.
        const hours = getTechDayHours(tech, format(selectedDate, 'yyyy-MM-dd'))
          || { open: tech.shift_start, close: tech.shift_end };
        const shiftTimeStr = (hours.open || tech.shift_start || '08:00').slice(0, 5);
        desiredStartTime = getTodaySafeStartTime(shiftTimeStr, selectedDayLabel, shopTimezone);
      }
    }

    // HIGH 1.2: Past-time guard — desiredStartTime is already UTC ISO string, direct UTC comparison
    const todayInShopTz = getTodayInShopTz(shopTimezone);
    if (selectedDayLabel === todayInShopTz && desiredStartTime) {
      if (new Date(desiredStartTime).getTime() < clock.nowMs()) {
        toast({
          title: 'Cannot schedule in the past',
          description: 'Please select a future time slot.',
          variant: 'destructive',
        });
        setActiveTicket(null);
        setDragOverTechId(null);
        setPreviewStartTime(null);
        setHoverIndex(-1);
        setIsDragging(false);
        return;
      }
    }

    // R41 FIX-3: In-flight lock — prevent double-drag on unconfirmed state
    if (inFlightReassignIds.current.has(String(ticketId))) {
      console.warn('[Dashboard] Ticket already in-flight, ignoring drag:', ticketId);
      setIsDragging(false);
      return;
    }
    inFlightReassignIds.current.add(String(ticketId));

    // Store original for rollback
    const originalTicket = tickets.find(t => t.id === ticketId);
    if (originalTicket) {
      setOriginalTicketData({ ...originalTicket });
    }
    setPendingMoveTicketId(String(ticketId));

    // Optimistically update UI - move ticket to new position
    setTickets(prev => prev.map(t => 
      t.id === ticketId 
        ? {
            ...t,
            assignedTechId: newTechId,
            assigned_tech_id: newTechId,
            start_time: desiredStartTime || t.start_time,
            startTime: desiredStartTime ? formatShopTime(desiredStartTime, shopTimezone, 'short') : t.startTime
          }
        : t
    ));

    // SCH-4: commit the reassign with a concrete moveReason. Wrapped so a
    // cross-tech override can DEFER the commit until the operator picks a
    // reason (governance + preference-learning signal); routine moves call it
    // immediately with an operational reason. Body unchanged except moveReason
    // is now a parameter instead of the former hardcoded 'drag-drop' literal.
    const runReassign = async (moveReason: MoveReason) => {
    // Structured pre-call log — pairs with [REASSIGN][drag] entries on the
    // server so a drag's start-time round-trip is visible end-to-end.
    // Useful for diagnosing "preview said 7:25 but persisted 7:40" cases.
    console.log('[Dashboard.handleDragEnd] sending reassign-ticket', {
      ticketId,
      sourceTechId: oldTechId,
      targetTechId: newTechId,
      scheduledDate: selectedDayLabel,
      desiredStartTime,
      previewedStartLocal: desiredStartTime
        ? formatShopTime(desiredStartTime, shopTimezone, 'short')
        : null,
      manualOverride: true,
    });

    try {
      // Use the reassign-ticket edge function with desired start time
      const { data: reassignResult, error: reassignError } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId,
          targetTechId: newTechId,
          scheduledDate: selectedDayLabel,
          desiredStartTime: desiredStartTime, // Already UTC ISO string
          manualOverride: true, // User-initiated drag & drop
          moveReason, // SCH-4: operator-chosen (cross-tech) or operational reason
          // Core-100 R2 (CORE-AD): a cross-tech drag is a person choosing the technician.
          ...(manualMoveRequiresReason(oldTechId, newTechId) ? { selectionSurface: 'dayboard_cross_tech_drag' } : {}),
        }
      });

      // Match what came back so a preview/persisted disagreement is loud
      // and structured in the console.
      if (reassignResult) {
        console.log('[Dashboard.handleDragEnd] reassign-ticket response', {
          ticketId,
          desiredStartTimeSent: desiredStartTime,
          newStartTimeReturned: (reassignResult as any).newStartTime ?? null,
          cascadedCount: (reassignResult as any).cascadedCount ?? 0,
          success: (reassignResult as any).success,
        });
      }

      if (reassignError) throw reassignError;

      if (!reassignResult?.success) {
        throw new Error(reassignResult?.error || 'Reassignment failed');
      }

      const cascadedCount = reassignResult?.cascadedCount || 0;
      const movedPartsCount = reassignResult?.movedPartsCount || 1;
      const cascadeMsg = cascadedCount > 0 ? ` (cascaded ${cascadedCount} tickets)` : '';
      const partsMsg = movedPartsCount > 1 ? `${movedPartsCount} split ticket parts` : 'Ticket';
      
      // Log advisor override event (Core Tier Pensieve logging)
      if (user && activeShop) {
        logAdvisorOverride({
          ticketId: String(ticketId),
          shopId: activeShop.id,
          userId: user.id,
          fromTechId: oldTechId || null,
          toTechId: newTechId,
        }).catch(err => console.warn('[PENSIEVE] Failed to log advisor override:', err));
      }
      
      // Invalidate ticket history to show event immediately
      queryClient.invalidateQueries({ queryKey: ['ticket-history', String(ticketId)] });
      
      // Clear pending state - ticket becomes solid
      setPendingMoveTicketId(null);
      setOriginalTicketData(null);
      
      toast({
        title: movedPartsCount > 1 ? 'Split Ticket Reassigned' : 'Ticket Reassigned',
        description: `${partsMsg} moved to ${tech.name}${cascadeMsg}`,
      });

      // Cross-device + local Dayboard invalidation. The helper dispatches
      // the local `ticket-updated` event (sender refetch) AND sends the
      // shop-scoped broadcast (other-device refetch) in a single call.
      // See src/utils/dayboardInvalidation.ts for the rationale and
      // failure-mode handling.
      void notifyDayboardInvalidated({
        reason: 'drag-drop-reassign',
        shopId: activeShop.id,
        ticketId: String(ticketId),
        scheduledDate: selectedDayLabel,
        metadata: { fromTechId: oldTechId || null, toTechId: newTechId },
      });
    } catch (error: any) {
      // Rollback to original position on error
      if (originalTicketData) {
        setTickets(prev => prev.map(t =>
          t.id === ticketId ? originalTicketData : t
        ));
      }
      setPendingMoveTicketId(null);
      setOriginalTicketData(null);

      // supabase-js wraps non-2xx Edge Function responses as
      // FunctionsHttpError with the raw Response on `.context`. The
      // default `error.message` is the unhelpful generic
      // "Edge Function returned a non-2xx status code"; the real reason
      // (e.g. `"targetTechId is required when not moving to parking lot"`)
      // lives in the response body. Read it once so the toast names the
      // actual failure and the dev console gets a fully-attributed log.
      let description: string = error?.message || 'Failed to reassign ticket';
      let bodyForLog: unknown = null;
      try {
        const ctx = (error as { context?: Response })?.context;
        if (ctx && typeof ctx.clone === 'function' && typeof ctx.json === 'function') {
          const body = await ctx.clone().json();
          bodyForLog = body;
          if (body?.error) {
            description = body.code
              ? `${body.error} (${body.code})`
              : String(body.error);
          }
        }
      } catch { /* keep generic */ }

      console.error('[Dashboard.handleDragEnd] reassign-ticket failed', {
        ticketId,
        sourceTechId: oldTechId,
        targetTechId: newTechId,
        desiredStartTime,
        scheduledDate: selectedDayLabel,
        moveReason,
        responseBody: bodyForLog,
        error,
      });

      toast({
        title: 'Reassign failed',
        description,
        variant: 'destructive',
      });
    } finally {
      inFlightReassignIds.current.delete(String(ticketId));
      setActiveTicket(null);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      setIsDragging(false);
    }
    }; // end runReassign

    // SCH-4: a genuine cross-tech manual move requires an operator reason
    // (override governance + preference-learning). Defer the commit, surface
    // the dialog, and clear the transient drag overlay now that the drop
    // landed. Routine moves (same-tech reorder / parked→tech assignment) are
    // committed immediately with an operational reason — no dialog friction.
    if (manualMoveRequiresReason(oldTechId, newTechId)) {
      const fromTech = technicians.find(t => t.id === oldTechId);
      pendingReasonMoveRef.current = {
        run: runReassign,
        cancel: () => {
          if (originalTicket) {
            setTickets(prev => prev.map(t => (t.id === ticketId ? { ...originalTicket } : t)));
          }
          setPendingMoveTicketId(null);
          setOriginalTicketData(null);
          inFlightReassignIds.current.delete(String(ticketId));
        },
      };
      setMoveReasonMeta({
        fromTechName: fromTech?.name,
        toTechName: tech?.name,
        ticketLabel:
          (ticket as any).ticket_number || (ticket as any).customer_name || undefined,
      });
      setMoveReasonOpen(true);
      // Drop landed — clear the transient drag-overlay UI. The deferred commit
      // owns pendingMoveTicketId / originalTicketData / in-flight until resolved.
      setActiveTicket(null);
      setDragOverTechId(null);
      setPreviewStartTime(null);
      setHoverIndex(-1);
      setIsDragging(false);
      return;
    }

    await runReassign(DEFAULT_OPERATIONAL_MOVE_REASON);
  };

  // SCH-4: resolve/cancel the deferred cross-tech move once the operator picks
  // (or dismisses) a reason. Ref is nulled FIRST so the dialog's trailing
  // onOpenChange(false) cannot double-fire cancel after a confirm/cancel.
  const handleMoveReasonConfirm = (reason: MoveReason) => {
    const pending = pendingReasonMoveRef.current;
    pendingReasonMoveRef.current = null;
    setMoveReasonOpen(false);
    setMoveReasonMeta(null);
    if (pending) void pending.run(reason);
  };
  const handleMoveReasonCancel = () => {
    const pending = pendingReasonMoveRef.current;
    pendingReasonMoveRef.current = null;
    setMoveReasonOpen(false);
    setMoveReasonMeta(null);
    if (pending) pending.cancel();
  };

  // Filter tickets by selected date — uses canonical `selectedDayLabel`
  // (shop-canonical YYYY-MM-DD) for `scheduled_date` string comparison.
  // DCC-A2 closure: removed the stale historical-TODO marker that had
  // requested this exact migration; the migration is satisfied below
  // at the `t.scheduled_date !== selectedDayLabel` check.
  const ticketsForSelectedDate = useMemo(() => {
    return tickets.filter(t => {
      // DMC-D3c-1: canonical deleted-state check (both reps).
      if (isTicketDeleted(t)) return false;
      if (t.is_estimate) return false;
      // NOV3 (A3): work still RUNNING on an earlier day is shown on TODAY's board so it can be paused or stopped.
      if (!isEarlierDayRunningOnToday(t)) {
        if (t.scheduled_date !== selectedDayLabel) return false;
      }
      
      if (isMultiTeamEnabled && selectedTeamFilter !== 'all') {
        return t.team_id === selectedTeamFilter;
      }
      
      return true;
    });
  }, [tickets, selectedDayLabel, isMultiTeamEnabled, selectedTeamFilter, shopTimezone]);  // eslint-disable-line react-hooks/exhaustive-deps -- NOV3: isEarlierDayRunningOnToday reads selectedDayLabel + shopTimezone

  // parking_lot flag no longer excludes tickets from Dayboard — all created tickets appear here
  const scheduledTickets = ticketsForSelectedDate;

  // Oct 20 addendum (F2): a deep link that asked to OPEN a ticket on this day for which the board drew no card (a
  // departed / returned repair — Blocked — or an unassigned continuation day) hosts that ticket's own Command Center
  // here, instead of silently doing nothing. Same card component, same popup, same RLS-read row; nothing new is read.
  // The hosted row is SNAPSHOT when hosting starts: a later refetch that briefly lacks it (a racing day fetch) must not
  // unmount the host — its Command Center would close and could not reopen (the card already consumed openDetails).
  const [deepLinkCcHost, setDeepLinkCcHost] = useState<any | null>(null);
  // A new link (even to the same ticket, after the last one was consumed) starts a fresh host, so it opens again.
  useEffect(() => { if (deepLinkTicketId) setDeepLinkCcHost(null); }, [deepLinkTicketId]);
  // Keyed on whether the row is PRESENT, not on the array identity: realtime refreshes must not keep restarting the
  // timer until the highlight safety net clears the link.
  const deepLinkRowOnDay = deepLinkTicketId ? ticketsForSelectedDate.find((t) => t.id === deepLinkTicketId) ?? null : null;
  const deepLinkRowRef = useRef<any | null>(null);
  deepLinkRowRef.current = deepLinkRowOnDay;
  const deepLinkRowPresent = !!deepLinkRowOnDay;
  useEffect(() => {
    if (!deepLinkTicketId || !deepLinkWantsDetails || isLoading || !deepLinkRowPresent) return undefined;
    const id = deepLinkTicketId;
    // Give the board one beat to mount (and a card to consume openDetails) before deciding no card exists.
    const timer = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      if (needsDeepLinkCommandCenterHost({
        ticketId: id,
        stillWantsDetails: params.get('highlight') === id && params.get('openDetails') === 'true',
        rowOnSelectedDay: true,
        cardMounted: !!document.getElementById(`ticket-${id}`),
      }) && deepLinkRowRef.current?.id === id) setDeepLinkCcHost(deepLinkRowRef.current);
    }, 1500);
    return () => clearTimeout(timer);
  }, [deepLinkTicketId, deepLinkWantsDetails, isLoading, deepLinkRowPresent]);
  const deepLinkCcHostRow = deepLinkCcHost ? tickets.find((t) => t.id === deepLinkCcHost.id) ?? deepLinkCcHost : null;

  // ---------------------------------------------------------------
  // Slice 5D-12 Batch 2 — board-level advisor bay-hold provider.
  //
  // Mounted at the Dashboard level so TicketCards on the Dayboard
  // and TechnicianColumns receive pre-computed advisor bay-hold
  // values WITHOUT each card running its own custody-events query
  // (the explicit anti-N+1 design rule from Batch 1).
  //
  // Flag-gated: when `advisorBayOccupancyOnDayboard` is OFF the
  // hook returns null and pays ZERO query cost. The map is also
  // null when no tickets are visible.
  // ---------------------------------------------------------------
  const advisorBayOccupancyOnDayboardEnabled = useFeatureFlag('advisorBayOccupancyOnDayboard');
  // Slice 5D-14 Batch 2.5: gate the predicted-path branch inside the
  // provider. Predicted requires BOTH flags ON: the bay-occupancy
  // display flag (which enables the entire provider), AND the
  // advisor-timing-analytics flag (which authorises consumption of
  // per-advisor bucket metrics — same gate the single-ticket
  // lifecycle card uses).
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const advisorBayHoldTickets = useMemo(
    () =>
      ticketsForSelectedDate.map((t: any) => {
        // Pre-flatten ticket_jobs for classifyTicketJobMix so the
        // provider can run job-mix classification without a second
        // ticket_jobs query. Dashboard already loaded the rich data
        // with `job:jobs(*)` joins (see fetchTicketsForSelectedDate
        // above).
        const flat_jobs = Array.isArray(t.ticket_jobs)
          ? t.ticket_jobs.map((tj: any) => ({
              category: tj?.category ?? tj?.job?.category ?? null,
              submenu: tj?.submenu ?? tj?.job?.submenu ?? null,
              proficiency_category:
                tj?.proficiency_category ?? tj?.job?.proficiency_category ?? null,
              name: tj?.name ?? tj?.job?.name ?? null,
            }))
          : Array.isArray(t.jobs)
            ? t.jobs.map((j: any) => ({
                category: j?.category ?? null,
                submenu: j?.submenu ?? null,
                proficiency_category: j?.proficiency_category ?? null,
                name: j?.name ?? null,
              }))
            : null;
        return {
          id: t.id as string,
          created_at: (t.created_at as string | null | undefined) ?? null,
          terminal_at: (t.completed_at as string | null | undefined) ?? null,
          is_comeback: (t.is_comeback as boolean | null | undefined) ?? null,
          flat_jobs,
        };
      }),
    [ticketsForSelectedDate],
  );
  const advisorBayHoldByTicketId = useBoardAdvisorBayHold({
    shopId: activeShop?.id ?? null,
    tickets: advisorBayHoldTickets,
    enabled: advisorBayOccupancyOnDayboardEnabled,
    advisorTimingAnalyticsEnabled,
  });
  // Phase 7.3: board-level split label data map. Computed ONCE per
  // `tickets` change and threaded to TechnicianColumn + each parking-
  // lot/drag-overlay TicketCard so SplitLink can render real
  // "Part X of Y" labels without per-card reconstruction. Mirrors the
  // `advisorBayHoldByTicketId` pattern above.
  const splitLabelDataByTicketId = useMemo(
    () => buildSplitPartLabelDataMap(tickets as unknown as SplitGroupTicketShape[]),
    [tickets],
  );
  // W5: one read of the shop's open unattended operations, threaded to every TechnicianColumn (same lift pattern).
  const openUnattendedByTicketId = useOpenUnattendedOperations(activeShop?.id ?? null);
  // B-T1: the server's PAST_EXPECTED_END / OPEN_AT_CLOSE flags ride on each open operation (display only).
  const unattendedFlagsByOperationId = useUnattendedOperationFlags(activeShop?.id ?? null);
  const unattendedByTicketId = useMemo(
    () => attachUnattendedFlags(openUnattendedByTicketId, unattendedFlagsByOperationId) as typeof openUnattendedByTicketId,
    [openUnattendedByTicketId, unattendedFlagsByOperationId],
  );
  // IV-D-28 R2 FIX-1: memoize the three status-count scans. Previously each
  // scheduledTickets.filter(...).length ran every render; now a single pass
  // produces all three counts, only recomputed when scheduledTickets changes.
  const { activeTickets, completedToday, pausedTickets } = useMemo(() => {
    let active = 0, completed = 0, paused = 0;
    for (const t of scheduledTickets) {
      if (t.status === 'Active') active++;
      else if (t.status === 'Completed') completed++;
      else if (t.status === 'Paused') paused++;
    }
    return { activeTickets: active, completedToday: completed, pausedTickets: paused };
  }, [scheduledTickets]);
  // L-1: Standardize to schedule-based presence — matches Overview stat (isTechnicianScheduledPresent)
  // IV-D-28 R2 FIX-2: memoize per-tech schedule-presence scan (previously ran
  // every render and called isTechnicianScheduledPresent per tech).
  const presentTechs = useMemo(() => {
    let count = 0;
    for (const tech of sortedTechnicians) {
      if (!tech.hours) continue;
      if (isTechnicianScheduledPresent(
        { hours: tech.hours, time_off: tech.time_off || [] },
        selectedDate
      )) count++;
    }
    return count;
  }, [sortedTechnicians, selectedDate]);

  // M-1: Hoisted from inline JSX useMemo — conditional useMemo calls violate Rules of Hooks
  const presentTechsLabel = useMemo(() => {
    const present = technicians.filter(tech => {
      if (!tech.hours) return false;
      const schedule = {
        hours: tech.hours,
        time_off: tech.time_off || []
      };
      return isTechnicianScheduledPresent(schedule, selectedDate);
    }).length;
    return `${present}/${technicians.length}`;
  }, [technicians, selectedDate]);

  // C-1: Safe conditional return — all hooks above are already declared unconditionally
  if (shouldShowNoAccess) return <NoAccess />;

  if (error) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-destructive mb-2">Error Loading Dashboard</h2>
            <p className="text-muted-foreground">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded"
            >
              Reload Page
            </button>
          </div>
        </main>
      </div>
    );
  }

  const colWidthClasses = compactView ? "min-w-[240px] max-w-[280px]" : "min-w-[320px] max-w-[400px]";
  const cardScaleClass = compactView ? "scale-90" : "";

  return (
    <ReminderCountsProvider shopId={activeShop?.id}>
    <div className="relative flex flex-col min-h-screen bg-background">
      <div className="tech-grid" />
      <div className="relative z-10 flex flex-col h-full">
      <WelcomeDialog />
      <WorkSwitchHost shopId={activeShop?.id ?? null} />
      <TopBar />
      <LiveUpdatesBanner state={liveState} timeZone={shopTimezone} />
      <ScheduleReconcileBanner reconcile={runLongReconcile} live={liveState} timeZone={shopTimezone} />
      {/* L-4: Invalid IANA timezone banner — prevents silent scheduling corruption */}
      {!isTimezoneValid && (
        <div className="bg-destructive/10 border-b border-destructive text-destructive text-sm px-4 py-2 text-center flex items-center justify-center gap-2">
          <span>⚠️</span>
          <span>Invalid shop timezone configuration — scheduling times may be incorrect. Please update your Shop Profile.</span>
        </div>
      )}
      
      <main className="flex-1 overflow-y-auto">
        <Tabs value={currentTab} onValueChange={(v) => { setCurrentTab(v); navigate(`/?tab=${v}`); }} className="h-full flex flex-col">
          {/* Tab Navigation */}
          <div className="border-b bg-card px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center justify-between gap-2 md:gap-4 min-w-max">
              <div className="flex items-center gap-2">
                {/* OptiBoard navigation button - left of tabs */}
                {(isOwner || isManager || isServiceWriter) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/optiboard')}
                    className="gap-1.5 text-xs md:text-sm"
                    data-testid="optiboard-link"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    {isMobile ? 'Opti' : 'OptiBoard'}
                  </Button>
                )}
              <TabsList className="flex-shrink-0 overflow-x-auto scrollbar-none">
                {canViewOverview && (
                  <TabsTrigger value="overview" data-testid="overview-tab" className="text-xs md:text-sm">
                    {isMobile ? 'Over.' : 'Overview'}
                  </TabsTrigger>
                )}
                <TabsTrigger value="dayboard" data-testid="dayboard-tab" className="text-xs md:text-sm">Dayboard</TabsTrigger>
                <TabsTrigger value="specialty-bays" data-testid="specialty-bays-tab" className="text-xs md:text-sm">
                  {isMobile ? 'Specialty' : 'Specialty Bays'}
                </TabsTrigger>
                {/* Hide parking lot tab on mobile, show drawer button instead */}
                {!isMobile && (
                  <TabsTrigger value="parking-lot" data-testid="parking-lot-tab" className="text-xs md:text-sm">
                    <Car className="h-4 w-4 mr-2" />
                    Parking Lot ({ticketsForSelectedDate.filter(t => isOnBacklogBoard(t)).length})
                  </TabsTrigger>
                )}
              </TabsList>
              </div>
              
              <div className="flex items-center gap-1 md:gap-2">
                {/* Mobile parking lot drawer button */}
                {isMobile && (
                  <Drawer open={showParkingLotDrawer} onOpenChange={setShowParkingLotDrawer}>
                    <DrawerTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1 text-xs">
                        <Car className="h-4 w-4" />
                        <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                          {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t)).length}
                        </Badge>
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="max-h-[85vh]">
                      <DrawerHeader>
                        <DrawerTitle>Parking Lot</DrawerTitle>
                      </DrawerHeader>
                      <div className="overflow-y-auto px-4 pb-8">
                        {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t)).length === 0 ? (
                          <div className="text-center py-12 text-muted-foreground">
                            <Car className="h-16 w-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">Parking lot is empty</p>
                            <p className="text-sm mt-2">Completed and parked tickets will appear here</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Completed Tickets (OCT28 RECOVERY #8: no status-coloured wrapper; the card's border is the run state) */}
                            {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t) && t.status === 'Completed').length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-green-600 mb-2">Completed</h4>
                                <div className="space-y-2">
                                  {ticketsForSelectedDate
                                    .filter(t => isOnBacklogBoard(t) && t.status === 'Completed')
                                    .map(ticket => {
                                      const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                                      return (
                                        <div key={ticket.id} className="rounded-lg">
                                          <TicketCard 
                                            ticket={{
                                              ...ticket,
                                              jobs: ticket.jobs,
                                              assignedTechId: ticket.assigned_tech_id,
                                              customerName: ticket.customer_name,
                                              customerPhone: ticket.customer_phone,
                                              dueOut: ticket.due_out,
                                              isComeback: ticket.is_comeback,
                                              startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                              predictedMinutes: ticket.predicted_minutes,
                                              pauseReason: ticket.pause_reason,
                                              actualMinutes: ticket.actual_minutes
                                            }}
                                            technician={tech}
                                            isInParkingLot={true}
                                            isHighlighted={highlightedTicketId === ticket.id}
                                            technicians={technicians.map(t => ({ 
                                              id: t.id, 
                                              name: t.name, 
                                              is_present: t.is_present 
                                            }))}
                                            hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                            hideKebabMenu={restrictToOwnColumn}
                                            onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                            advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                            onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                          />
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                            {/* Paused Tickets */}
                            {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t) && t.status === 'Paused').length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-orange-600 mb-2">Paused</h4>
                                <div className="space-y-2">
                                  {ticketsForSelectedDate
                                    .filter(t => isOnBacklogBoard(t) && t.status === 'Paused')
                                    .map(ticket => {
                                      const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                                      return (
                                        <div key={ticket.id} className="rounded-lg">
                                          <TicketCard 
                                            ticket={{
                                              ...ticket,
                                              jobs: ticket.jobs,
                                              assignedTechId: ticket.assigned_tech_id,
                                              customerName: ticket.customer_name,
                                              customerPhone: ticket.customer_phone,
                                              dueOut: ticket.due_out,
                                              isComeback: ticket.is_comeback,
                                              startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                              predictedMinutes: ticket.predicted_minutes,
                                              pauseReason: ticket.pause_reason,
                                              actualMinutes: ticket.actual_minutes
                                            }}
                                            technician={tech}
                                            isInParkingLot={true}
                                            isHighlighted={highlightedTicketId === ticket.id}
                                            technicians={technicians.map(t => ({ 
                                              id: t.id, 
                                              name: t.name, 
                                              is_present: t.is_present 
                                            }))}
                                            hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                            hideKebabMenu={restrictToOwnColumn}
                                            onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                            advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                            onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                          />
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                            {/* Active Tickets */}
                            {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t) && t.status === 'Active').length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-blue-600 mb-2">Active</h4>
                                <div className="space-y-2">
                                  {ticketsForSelectedDate
                                    .filter(t => isOnBacklogBoard(t) && t.status === 'Active')
                                    .map(ticket => {
                                      const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                                      return (
                                        <div key={ticket.id} className="rounded-lg">
                                          <TicketCard 
                                            ticket={{
                                              ...ticket,
                                              jobs: ticket.jobs,
                                              assignedTechId: ticket.assigned_tech_id,
                                              customerName: ticket.customer_name,
                                              customerPhone: ticket.customer_phone,
                                              dueOut: ticket.due_out,
                                              isComeback: ticket.is_comeback,
                                              startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                              predictedMinutes: ticket.predicted_minutes,
                                              pauseReason: ticket.pause_reason,
                                              actualMinutes: ticket.actual_minutes
                                            }}
                                            technician={tech}
                                            isInParkingLot={true}
                                            isHighlighted={highlightedTicketId === ticket.id}
                                            technicians={technicians.map(t => ({ 
                                              id: t.id, 
                                              name: t.name, 
                                              is_present: t.is_present 
                                            }))}
                                            hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                            hideKebabMenu={restrictToOwnColumn}
                                            onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                            advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                            onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                          />
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </DrawerContent>
                  </Drawer>
                )}
                <WeatherPill />
                <TimeClockButton refreshKey={timeClockRefreshKey} />
                <NotificationPanel
                  notifications={notifications}
                  onClearAll={() => {
                    setNotifications([]);
                    localStorage.setItem('notifications', '[]');
                  }}
                  onMarkAsRead={(id) => {
                    const updated = notifications.map(n => 
                      n.id === id ? { ...n, read: true } : n
                    );
                    setNotifications(updated);
                    localStorage.setItem('notifications', JSON.stringify(updated));
                  }}
                  onViewImportReport={(report) => {
                    setSelectedReport(report);
                    setShowImportReport(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Weather Alert Banner */}
          <WeatherAlertBanner openAlerts={weatherAlerts} isLoading={weatherLoading} onViewAlerts={() => setShowWeatherAlerts(true)} />

          {/* Overview Tab */}
          {canViewOverview && (
          <TabsContent value="overview" className="flex-1 overflow-visible p-0 mt-0">
            <div className="max-w-7xl mx-auto w-full space-y-4 px-6 pt-0">
              <div>
                <h2 className="text-2xl font-bold mb-2 mt-0">Shop Overview</h2>
                <p className="text-muted-foreground">Real-time performance and status monitoring</p>
              </div>

              {/* Stats Grid */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <StatCard
                  title="Active Tickets"
                  value={activeTickets}
                  subtitle="Currently working"
                  icon={Clock}
                />
                <StatCard
                  title="Completed Today"
                  value={completedToday}
                  subtitle="Jobs finished"
                  icon={CheckCircle2}
                />
                <StatCard
                  title="Paused"
                  value={pausedTickets}
                  subtitle="Awaiting action"
                  icon={AlertTriangle}
                />
                <StatCard
                  title="Technicians Present"
                  value={presentTechsLabel}
                  subtitle="On duty today"
                  icon={Users}
                />
                <ShopUtilizationCard
                  shopId={activeShop?.id || ''}
                  selectedDate={selectedDate}
                  selectedDayLabel={selectedDayLabel}
                />
                <RequireFeature feature="advancedAnalytics">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Catalyst Analytics Preview</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      Deeper technician performance, job mix, and OBLI insights live here
                      when you're on Catalyst or Enterprise.
                    </CardContent>
                  </Card>
                </RequireFeature>
              </div>

              {/* R5 — Today's Financial Snapshot (manager-gated, self-hides if not allowed) */}
              <div className="mt-6">
                <FinancialSnapshotCard selectedDayLabel={selectedDayLabel} />
              </div>

              {/* OBLI Bucket - Full Width */}
              <div className="mt-6">
              <OBLIBucket
                  mechanicalTechnicianIds={mechanicalTechnicianIds}
                  capacityInputsReady={obliCapacityInputsReady}
                  technicians={capacityTechnicians}
                  tickets={tickets}
                  selectedDate={selectedDate}
                  selectedDayLabel={selectedDayLabel}
                  shopHours={shopHours}
                  holidays={holidays}
                  fullWidth={true}
                  shopTimezone={shopTimezone}
                />
              </div>

              {/* Technician Utilization */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Technician Workload</h3>
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {technicians.map(tech => {
                      // Phase G1 — canonical workload model. Mirrors
                      // TechnicianColumn.tsx so the Overview card and the
                      // Dayboard tech-column header produce identical
                      // minutes for the same tech + ticket set.
                      //
                      // Per derivedTicketTime rules:
                      //   - unstarted → techOptiTime (scheduled wall time)
                      //   - active running → techOptiTime + runLong
                      //   - held-in-bay → techOptiTime + bay-blocking pause
                      //   - held-in-parking / non-bay hold → 0
                      //   - terminal → 0
                      const techTickets = ticketsForSelectedDate.filter(t =>
                        t.assigned_tech_id === tech.id &&
                        !t.parking_lot &&
                        t.status !== 'Completed' &&
                        // DMC-D3c-1: canonical deleted-state check (both reps).
                        !isTicketDeleted(t)
                      );

                      const _nowMs = clock.nowMs();
                      const totalBookMinutes = sumWorkloadMinutes(
                        techTickets.map((t) => {
                          const actualStartIso = (t as any).actual_start_time as string | null | undefined;
                          const liveElapsedSeconds = actualStartIso
                            ? Math.max(0, Math.floor((_nowMs - new Date(actualStartIso).getTime()) / 1000))
                            : 0;
                          const startIso = (t as any).start_time as string | null | undefined;
                          const startInstant = startIso ? new Date(startIso) : null;
                          return getDerivedTicketTime({
                            ticket: t as any,
                            technician: {
                              efficiencyPercentage: (tech as any).efficiency_percentage ?? 100,
                              bufferPercentage: resolvePlanningBufferPct((tech as any).buffer_percentage),
                              recurring_breaks: (tech as any).recurring_breaks ?? null,
                              non_recurring_breaks: (tech as any).non_recurring_breaks ?? null,
                              shift_start: (tech as any).shift_start ?? null,
                              shift_end: (tech as any).shift_end ?? null,
                            },
                            jobs: (t as any).jobs as any,
                            shopConfig: { willingnessRollPastEodMin: 0, eodRiskBandMin: 30 },
                            startInstant:
                              startInstant && Number.isFinite(startInstant.getTime())
                                ? startInstant
                                : null,
                            nowInstant: new Date(_nowMs),
                            liveElapsedSeconds,
                          });
                        }),
                      );

                      // CORE-AW §44. This read `capacity > 0` for both
                      // "present" and the workload denominator, so a
                      // technician whose hours nobody has recorded was
                      // dimmed and labelled "Off today" — a factual claim
                      // about a person, made from an absence of record.
                      // Core-100 R4 (CORE-AW): partial-day absences subtracted; a scheduled day whose absences
                      // could not be read is unknown, not full.
                      const scheduled = resolveTechnicianCapacity(
                        { ...tech, absence_intervals: absenceIntervals.byTech.get(String(tech.id)) ?? [] },
                        // Core-100 R4 W5 (CORE-AW Rel): the shop-local day label, not the device's reading of a Date.
                        selectedDayLabel,
                        // B-W4 rule 5: the numerator (totalBookMinutes = OptiTime workload) ALREADY carries efficiency and
                        // buffer; the 'adjusted' capacity applied both again (buffer counted twice). Wall minutes ÷ wall minutes.
                        'scheduled',
                      );
                      const capacity: typeof scheduled = absenceIntervals.status === 'error' && scheduled.state === 'known'
                        ? { state: 'unknown', schedule: scheduled.schedule }
                        : scheduled;
                      const workloadPercentage = capacityUtilizationPercent(totalBookMinutes, capacity);
                      const capacityUnknown = capacity.state === 'unknown';
                      const isPresentToday = capacity.state === 'known' && capacity.minutes > 0;

                      return (
                        <div key={tech.id} className={`p-4 border rounded-lg bg-card ${!isPresentToday && !capacityUnknown ? 'opacity-50' : ''}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium text-sm">{tech.name}</p>
                              <p className="text-xs text-muted-foreground">{tech.role}</p>
                            </div>
                            <div
                              className={`text-lg font-bold ${workloadPercentage === null ? 'text-muted-foreground' : workloadPercentage > 80 ? 'text-destructive' : workloadPercentage > 60 ? 'text-warning' : 'text-success'}`}
                              title={describeCapacity(capacity)}
                            >
                              {workloadPercentage === null ? '—' : `${workloadPercentage}%`}
                            </div>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            {workloadPercentage !== null && (
                              <div
                                className={`h-2 rounded-full transition-all ${workloadPercentage > 80 ? 'bg-destructive' : workloadPercentage > 60 ? 'bg-warning' : 'bg-success'}`}
                                style={{ width: `${Math.min(workloadPercentage, 100)}%` }}
                              />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2" data-testid="tech-capacity-line">
                            {capacityUnknown
                              ? 'Working hours not set — capacity unknown'
                              : isPresentToday
                                ? `${totalBookMinutes} min / ${capacity.minutes} min`
                                : 'Off today'}
                          </p>
                          {/* Core-100 R4 W5 (CORE-AW Prov): the minutes inherit the efficiency's basis — said beside them. */}
                          {isPresentToday && (() => {
                            const basis = describeCapacityBasis(tech);
                            return <p className="text-[11px] text-muted-foreground" data-testid="tech-capacity-basis" title={basis.detail}>{basis.text}</p>;
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Staff Presence Section */}
              <div className="mt-8">
                <StaffPresenceIndicator refreshKey={staffRefreshKey} />
              </div>

              {/* Completed Tickets Section */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Completed Tickets</h3>
                <CompletedTicketsSection selectedDate={selectedDate} />
              </div>
            </div>
          </TabsContent>
          )}

          {/* Dayboard Tab */}
          <TabsContent value="dayboard" className="flex-1 p-0 mt-0 flex flex-col">
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 py-3 border-b space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {/* Left side: View buttons + OBLI indicator */}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant={compactView ? "default" : "outline"}
                    size="sm"
                    onClick={toggleCompactView}
                    className="text-xs hidden md:flex"
                  >
                    Compact
                  </Button>
                  <Button
                    variant={isFullscreen ? "default" : "outline"}
                    size="sm"
                    onClick={toggleFullscreen}
                    className="text-xs hidden md:flex"
                  >
                    {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  </Button>
                  
                  {canAssignTickets && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setOpenCapacityOpen(true)} data-testid="open-capacity-button">
                      Open capacity
                    </Button>
                  )}
                  {canAssignTickets && openCapacityOpen && (
                    <OpenCapacityPanel open={openCapacityOpen} onOpenChange={setOpenCapacityOpen} />
                  )}
                  {canAssignTickets && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setNeedsAttentionOpen(true)} data-testid="needs-attention-button">
                      Needs attention
                    </Button>
                  )}
                  {canAssignTickets && needsAttentionOpen && (
                    <NeedsAttentionPanel open={needsAttentionOpen} onOpenChange={setNeedsAttentionOpen} />
                  )}

                  <DayboardLoadIndicator
                    mechanicalTechnicianIds={mechanicalTechnicianIds}
                    capacityInputsReady={obliCapacityInputsReady}
                    technicians={capacityTechnicians}
                    tickets={tickets}
                    selectedDate={selectedDate}
                    shopHours={shopHours}
                    holidays={holidays}
                  />
                  
                  {/* Team filter - only visible for multi-team shops */}
                  <TeamFilter 
                    value={selectedTeamFilter} 
                    onChange={setSelectedTeamFilter} 
                  />
                </div>
                
                {/* Right side: Optimizer buttons - hidden for now */}
              </div>
              
              <EODRolloverContainer />
              {/* OCT20 WS-ROLL (FD-11): unfinished rollover days from ended days, listed for a decision (no capacity). */}
              <RolloverDecisionRequiredStrip
                shopId={activeShop?.id}
                today={getTodayInShopTz(shopTimezone)}
                isToday={selectedDayLabel === getTodayInShopTz(shopTimezone)}
                timeZone={shopTimezone}
              />
              <EODBufferToast />
              <WaiterBumpWarningContainer />
              <SuggestionsDrawer dateISO={selectedDayLabel} />
              {absentReview && activeShop?.id ? (
                <AbsentTechReassignReview
                  open
                  onClose={() => setAbsentReview(null)}
                  technicianName={absentReview.technicianName}
                  tickets={absentReview.tickets}
                  shopId={activeShop.id}
                  selectedDayLabel={selectedDayLabel}
                  shopTimezone={shopTimezone}
                />
              ) : null}
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">Loading...</div>
            ) : (
              <DndContext 
                sensors={sensors} 
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                {formFactor === 'phone' ? (
                  // Phone: Single-tech carousel
                  <div className="flex-1 overflow-hidden py-4">
                    <Carousel className="w-full h-full" opts={{ watchDrag: activeTicket ? false : () => !isDayboardOverlayLocked() }} setApi={setCarouselApi}>
                      <CarouselContent className="-ml-4">
                        {/* MED 1.2: Use shared sorted technicians */}
                        {sortedTechnicians.map((tech, techIndex) => (
                          <CarouselItem key={tech.id} className="pl-4 basis-full">
                            <div className="h-full">
                              <TechnicianColumn
                                technician={{
                                  ...tech,
                                  skillTier: tech.skill_tier,
                                  bayAssignments: tech.bay_assignments,
                                  isPresent: tech.is_present,
                                  employmentStatus: tech.employment_status,
                                  shiftStart: tech.shift_start,
                                  shiftEnd: tech.shift_end,
                                  efficiencyPercentage: tech.efficiency_percentage,
                                  // CORE-AC: provenance travels with the value so the
                                  // OptiTime a card shows can say what it rests on.
                                  efficiencySource: (tech as any).efficiency_source ?? null,
                                  efficiencySampleSize: (tech as any).efficiency_sample_size ?? null,
                                  bufferPercentage: tech.buffer_percentage
                                }}
                                tickets={ticketsForSelectedDate.map(t => ({
                                  ...t,
                                  jobs: t.jobs,
                                  assignedTechId: t.assigned_tech_id,
                                  customerName: t.customer_name,
                                  customerPhone: t.customer_phone,
                                  dueOut: t.due_out,
                                  isComeback: t.is_comeback,
                                  startTime: formatShopTime(t.start_time, shopTimezone, 'short'),
                                  predictedMinutes: t.predicted_minutes,
                                  pauseReason: t.pause_reason,
                                  actualMinutes: t.actual_minutes,
                                  technician: tech,
                                  technicians: technicians.map(t => ({ 
                                    id: t.id, 
                                    name: t.name, 
                                    is_present: t.is_present,
                                    hours: t.hours,
                                    time_off: t.time_off
                                  }))
                                }))}
                                onReassignNeeded={handleReassignNeeded}
                                onKebabReassign={handleKebabMenuReassign}
                                compactView={false}
                                dragOverTechId={dragOverTechId}
                                previewStartTime={previewStartTime}
                                activeTicketId={activeTicket?.id}
                                hoverIndex={hoverIndex}
                                activeTicket={activeTicket}
                                isDraggable={false}
                                highlightedTicketId={highlightedTicketId}
                                pendingMoveTicketId={pendingMoveTicketId}
                                isFirstTechnician={techIndex === 0}
                                newOptiQueueTicketIds={newOptiQueueTicketIds}
                                timeClockEnabled={timeClockEnabled}
                                noShowGraceThresholdMin={noShowGraceThresholdMin}
                                readOnly={restrictToOwnColumn && tech.id !== myTechId}
                                advisorBayHoldByTicketId={advisorBayHoldByTicketId}
                                splitLabelDataByTicketId={splitLabelDataByTicketId}
                                unattendedByTicketId={unattendedByTicketId}
                                onTicketUpdated={(ticketId) => refreshSingleTicket(ticketId)}
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-2" />
                      <CarouselNext className="right-2" />
                    </Carousel>
                  </div>
                ) : formFactor === 'tablet' ? (
                  // Tablet: 2-column carousel
                  <div className="flex-1 overflow-hidden py-4">
                    <Carousel className="w-full h-full" opts={{ watchDrag: activeTicket ? false : () => !isDayboardOverlayLocked() }} setApi={setCarouselApi}>
                      <CarouselContent className="-ml-2">
                        {/* MED 1.2: Use shared sorted technicians */}
                        {sortedTechnicians.map((tech, techIndex) => (
                          <CarouselItem key={tech.id} className="pl-2 basis-1/2">
                            <div className="h-full">
                              <TechnicianColumn
                                technician={{
                                  ...tech,
                                  skillTier: tech.skill_tier,
                                  bayAssignments: tech.bay_assignments,
                                  isPresent: tech.is_present,
                                  employmentStatus: tech.employment_status,
                                  shiftStart: tech.shift_start,
                                  shiftEnd: tech.shift_end,
                                  efficiencyPercentage: tech.efficiency_percentage,
                                  bufferPercentage: tech.buffer_percentage
                                }}
                                tickets={ticketsForSelectedDate.map(t => ({
                                  ...t,
                                  jobs: t.jobs,
                                  assignedTechId: t.assigned_tech_id,
                                  customerName: t.customer_name,
                                  customerPhone: t.customer_phone,
                                  dueOut: t.due_out,
                                  isComeback: t.is_comeback,
                                  startTime: formatShopTime(t.start_time, shopTimezone, 'short'),
                                  predictedMinutes: t.predicted_minutes,
                                  pauseReason: t.pause_reason,
                                  actualMinutes: t.actual_minutes,
                                  technician: tech,
                                  technicians: technicians.map(t => ({ 
                                    id: t.id, 
                                    name: t.name, 
                                    is_present: t.is_present,
                                    hours: t.hours,
                                    time_off: t.time_off
                                  }))
                                }))}
                                onReassignNeeded={handleReassignNeeded}
                                onKebabReassign={handleKebabMenuReassign}
                                compactView={true}
                                dragOverTechId={dragOverTechId}
                                previewStartTime={previewStartTime}
                                activeTicketId={activeTicket?.id}
                                hoverIndex={hoverIndex}
                                activeTicket={activeTicket}
                                isDraggable={false}
                                highlightedTicketId={highlightedTicketId}
                                pendingMoveTicketId={pendingMoveTicketId}
                                isFirstTechnician={techIndex === 0}
                                newOptiQueueTicketIds={newOptiQueueTicketIds}
                                timeClockEnabled={timeClockEnabled}
                                noShowGraceThresholdMin={noShowGraceThresholdMin}
                                readOnly={restrictToOwnColumn && tech.id !== myTechId}
                                advisorBayHoldByTicketId={advisorBayHoldByTicketId}
                                splitLabelDataByTicketId={splitLabelDataByTicketId}
                                unattendedByTicketId={unattendedByTicketId}
                                onTicketUpdated={(ticketId) => refreshSingleTicket(ticketId)}
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-2" />
                      <CarouselNext className="right-2" />
                    </Carousel>
                  </div>
                ) : (
                  // Desktop: Horizontal scroll with all technicians
                <DayboardScrollArea className="h-[calc(100vh-12rem)] min-h-[600px]">
                  <div className={cn("p-2", isFullscreen && "p-4")}>
                     <div className="flex min-w-max gap-0">
                       {/* MED 1.2: Use shared sorted technicians */}
                       {sortedTechnicians.map((tech, techIndex) => {
                        // Calculate transform for cascade preview
                        let transformStyle = {};
                         if (activeTechnician && techHoverIndex !== -1) {
                            // MED 1.2: Use shared sorting helper
                            const activeIndex = getSortedTechnicians(
                              technicians,
                              selectedDate,
                              technicianOrder,
                              null
                            ).findIndex(t => t.id === activeTechnician.id);
                          
                          // Shift columns to preview the drop position
                          if (tech.id === activeTechnician.id) {
                            // The active tech being dragged - hide it
                            transformStyle = { opacity: 0.3 };
                          } else if (activeIndex < techHoverIndex && techIndex > activeIndex && techIndex <= techHoverIndex) {
                            // Shift left (active is before hover, shift items between them left)
                            transformStyle = { transform: `translateX(-${compactView ? 256 : 320}px)`, transition: 'transform 0.3s ease-out' };
                          } else if (activeIndex > techHoverIndex && techIndex >= techHoverIndex && techIndex < activeIndex) {
                            // Shift right (active is after hover, shift items between them right)
                            transformStyle = { transform: `translateX(${compactView ? 256 : 320}px)`, transition: 'transform 0.3s ease-out' };
                          }
                        }
                        
                        return (
                          <div 
                            key={tech.id} 
                            className={cn("flex-shrink-0", colWidthClasses)} 
                            style={transformStyle}
                            data-testid={`technician-column-${techIndex}`}
                          >
                          <div className={cn("h-full", cardScaleClass)}>
                            <TechnicianColumn
                              technician={{
                                ...tech,
                                skillTier: tech.skill_tier,
                                bayAssignments: tech.bay_assignments,
                                isPresent: tech.is_present,
                                employmentStatus: tech.employment_status,
                                shiftStart: tech.shift_start,
                                shiftEnd: tech.shift_end,
                                efficiencyPercentage: tech.efficiency_percentage,
                                bufferPercentage: tech.buffer_percentage
                              }}
                              onReassignNeeded={(tickets) => handleReassignNeeded(tickets, tech.name)}
                              onKebabReassign={handleKebabMenuReassign}
                              dragOverTechId={dragOverTechId}
                              previewStartTime={previewStartTime}
                              activeTicketId={activeTicket?.id}
                              hoverIndex={hoverIndex}
                              activeTicket={activeTicket}
                              isDraggable={canDragDropTickets}
                              highlightedTicketId={highlightedTicketId}
                              tickets={ticketsForSelectedDate.map(t => ({
                                ...t,
                                jobs: t.jobs, // Include jobs array for ticket card
                                assignedTechId: t.assigned_tech_id,
                                customerName: t.customer_name,
                                customerPhone: t.customer_phone,
                                dueOut: t.due_out,
                                isComeback: t.is_comeback,
                                startTime: formatShopTime(t.start_time, shopTimezone, 'short'),
                                predictedMinutes: t.predicted_minutes,
                                pauseReason: t.pause_reason,
                                actualMinutes: t.actual_minutes,
                                technician: tech, // Pass full technician object for break calculations
                                technicians: technicians.map(t => ({ 
                                  id: t.id, 
                                  name: t.name, 
                                  is_present: t.is_present,
                                  hours: t.hours,
                                  time_off: t.time_off
                                }))
                              }))}
                              compactView={compactView}
                              pendingMoveTicketId={pendingMoveTicketId}
                              isFirstTechnician={techIndex === 0}
                              newOptiQueueTicketIds={newOptiQueueTicketIds}
                              timeClockEnabled={timeClockEnabled}
                              noShowGraceThresholdMin={noShowGraceThresholdMin}
                              readOnly={restrictToOwnColumn && tech.id !== myTechId}
                              advisorBayHoldByTicketId={advisorBayHoldByTicketId}
                              splitLabelDataByTicketId={splitLabelDataByTicketId}
                              unattendedByTicketId={unattendedByTicketId}
                              onTicketUpdated={(ticketId) => refreshSingleTicket(ticketId)}
                            />
                          </div>
                        </div>
                        );
                      })}
                     </div>
                   </div>
                 </DayboardScrollArea>
                )}
                <DragOverlay>
                  {activeTechnician ? (
                    <div className={compactView ? "w-64 opacity-90" : "w-80 opacity-90"}>
                      <div className="bg-card border-2 border-primary rounded-lg shadow-2xl">
                        <div className="p-4 bg-muted/30">
                          <h3 className="font-semibold text-sm">{activeTechnician.name}</h3>
                          <p className="text-xs text-muted-foreground">{activeTechnician.role}</p>
                        </div>
                      </div>
                    </div>
                  ) : activeTicket ? (
                    <div className="opacity-80 rotate-3 scale-105">
                      <TicketCard 
                        ticket={{
                          ...activeTicket,
                          assignedTechId: activeTicket.assigned_tech_id,
                          customerName: activeTicket.customer_name,
                          customerPhone: activeTicket.customer_phone,
                          dueOut: activeTicket.due_out,
                          isComeback: activeTicket.is_comeback,
                          startTime: formatShopTime(activeTicket.start_time, shopTimezone, 'short'),
                          predictedMinutes: activeTicket.predicted_minutes,
                          pauseReason: activeTicket.pause_reason,
                          actualMinutes: activeTicket.actual_minutes
                        }}
                        splitLabelData={splitLabelDataByTicketId.get(activeTicket.id) ?? null}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </TabsContent>

          {/* Specialty Bays Tab */}
          <TabsContent
            value="specialty-bays"
            className="flex-1 overflow-visible p-0 mt-0"
          >
            <Suspense fallback={<div className="flex items-center justify-center text-muted-foreground">Loading...</div>}>
              <SpecialtyBays embedded selectedDate={selectedDate} />
            </Suspense>
          </TabsContent>

          {/* Parking Lot Tab */}
          <TabsContent value="parking-lot" className="flex-1 overflow-visible p-0 mt-0">
            <div data-testid="parking-lot-zone" className="max-w-7xl mx-auto w-full space-y-4 px-6 pt-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2 mt-0 mb-2">
                    <Car className="h-6 w-6" />
                    Parking Lot
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    Completed or parked tickets • Auto-rollover at midnight
                  </p>
                </div>
                <ToggleGroup type="single" value={parkingLotView} onValueChange={(v) => v && setParkingLotView(v as 'list' | 'map')}>
                  <ToggleGroupItem value="list" aria-label="List view">
                    <List className="h-4 w-4 mr-1" /> List
                  </ToggleGroupItem>
                  <ToggleGroupItem value="map" aria-label="Map view">
                    <MapIcon className="h-4 w-4 mr-1" /> Map
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              {parkingLotView === 'map' ? (
                <ParkingLotMapView shopId={activeShop?.id} />
              ) : isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : (
                  <>
                  {ticketsForSelectedDate.filter(t => isOnBacklogBoard(t)).length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Car className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Parking lot is empty</p>
                      <p className="text-sm mt-2">Completed and parked tickets will appear here</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-6">
                      {/* Completed Column (Left). OCT28 RECOVERY #8: the wrappers carry no status colour — the card's own run-state
                          border is the only one (a status-coloured frame around it contradicted it). */}
                      <div className="space-y-4">
                        {ticketsForSelectedDate
                          .filter(t => isOnBacklogBoard(t) && t.status === 'Completed')
                          .map(ticket => {
                            const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                            return (
                              <div key={ticket.id} data-testid="parking-lot-ticket-card" className="rounded-lg">
                                <TicketCard 
                                  ticket={{
                                    ...ticket,
                                    jobs: ticket.jobs,
                                    assignedTechId: ticket.assigned_tech_id,
                                    customerName: ticket.customer_name,
                                    customerPhone: ticket.customer_phone,
                                    dueOut: ticket.due_out,
                                    isComeback: ticket.is_comeback,
                                    startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                    predictedMinutes: ticket.predicted_minutes,
                                    pauseReason: ticket.pause_reason,
                                    actualMinutes: ticket.actual_minutes
                                  }}
                                  technician={tech}
                                  isInParkingLot={true}
                                  isHighlighted={highlightedTicketId === ticket.id}
                                  technicians={technicians.map(t => ({ 
                                    id: t.id, 
                                    name: t.name, 
                                    is_present: t.is_present 
                                  }))}
                                   hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                   hideKebabMenu={restrictToOwnColumn}
                                    onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                    parkingSpotLabel={(ticket as any).parking_spot?.label}
                                    advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                    onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                  />
                              </div>
                            );
                          })}
                      </div>

                      {/* Paused Column (Middle) - Orange Borders */}
                      <div className="space-y-4">
                        {ticketsForSelectedDate
                          .filter(t => isOnBacklogBoard(t) && t.status === 'Paused')
                          .map(ticket => {
                            const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                            return (
                              <div key={ticket.id} data-testid="parking-lot-ticket-card" className="rounded-lg">
                                <TicketCard 
                                  ticket={{
                                    ...ticket,
                                    jobs: ticket.jobs,
                                    assignedTechId: ticket.assigned_tech_id,
                                    customerName: ticket.customer_name,
                                    customerPhone: ticket.customer_phone,
                                    dueOut: ticket.due_out,
                                    isComeback: ticket.is_comeback,
                                    startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                    predictedMinutes: ticket.predicted_minutes,
                                    pauseReason: ticket.pause_reason,
                                    actualMinutes: ticket.actual_minutes
                                  }}
                                  technician={tech}
                                  isInParkingLot={true}
                                  isHighlighted={highlightedTicketId === ticket.id}
                                  technicians={technicians.map(t => ({ 
                                    id: t.id, 
                                    name: t.name, 
                                    is_present: t.is_present 
                                  }))}
                                   hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                   hideKebabMenu={restrictToOwnColumn}
                                    onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                    parkingSpotLabel={(ticket as any).parking_spot?.label}
                                    advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                    onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                  />
                              </div>
                            );
                          })}
                      </div>

                      {/* Active Column (Right) - Blue Borders */}
                      <div className="space-y-4">
                        {ticketsForSelectedDate
                          .filter(t => isOnBacklogBoard(t) && t.status === 'Active')
                          .map(ticket => {
                            const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
                            return (
                              <div key={ticket.id} data-testid="parking-lot-ticket-card" className="rounded-lg">
                                <TicketCard 
                                  ticket={{
                                    ...ticket,
                                    jobs: ticket.jobs,
                                    assignedTechId: ticket.assigned_tech_id,
                                    customerName: ticket.customer_name,
                                    customerPhone: ticket.customer_phone,
                                    dueOut: ticket.due_out,
                                    isComeback: ticket.is_comeback,
                                    startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                                    predictedMinutes: ticket.predicted_minutes,
                                    pauseReason: ticket.pause_reason,
                                    actualMinutes: ticket.actual_minutes
                                  }}
                                  technician={tech}
                                  isInParkingLot={true}
                                  isHighlighted={highlightedTicketId === ticket.id}
                                  technicians={technicians.map(t => ({ 
                                    id: t.id, 
                                    name: t.name, 
                                    is_present: t.is_present 
                                  }))}
                                   hideTimerControls={restrictToOwnColumn && !mayResumeReadyInterruptedRepair(ticket, myTechId)}
                                   hideKebabMenu={restrictToOwnColumn}
                                    onReassign={canAssignTickets ? handleKebabMenuReassign : undefined}
                                    parkingSpotLabel={(ticket as any).parking_spot?.label}
                                    advisorBayHoldData={advisorBayHoldByTicketId?.get(ticket.id) ?? null}
                                    splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                                    onTicketUpdated={() => refreshSingleTicket(ticket.id)}
                                  />
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      <ImportReport
        open={showImportReport}
        onOpenChange={setShowImportReport}
        report={selectedReport}
      />
      
      <WeatherAlertPanel
        open={showWeatherAlerts}
        onOpenChange={setShowWeatherAlerts}
        openAlerts={weatherAlerts}
        dismissAlert={dismissAlert}
        resolveAlert={resolveAlert}
        isLoading={weatherLoading}
      />

      <ParkingSpotPickerDialog
        isOpen={!!completionParkingPrompt}
        onClose={() => setCompletionParkingPrompt(null)}
        ticketId={completionParkingPrompt?.ticketId ?? null}
        shopId={completionParkingPrompt?.shopId ?? ''}
        selectionOnly={true}
        onSelect={async (spotId) => {
          if (completionParkingPrompt) {
            const result = await parkCompletedTicket(completionParkingPrompt.ticketId, spotId);
            if (!result.ok) {
              toast({
                title: 'Could not park vehicle',
                description: result.reason || 'The selected spot is unavailable.',
                variant: 'destructive',
              });
            }
            void notifyDayboardInvalidated({
              reason: 'kebab-parking-lot',
              shopId: completionParkingPrompt.shopId,
              ticketId: completionParkingPrompt.ticketId,
              metadata: { kind: 'completion-parking' },
            });
          }
        }}
      />

      {/* SCH-4: operator reason capture for manual cross-tech moves. Ref is
          nulled by confirm/cancel first, so this onOpenChange only cancels on
          an external close (ESC / overlay) while a move is still pending. */}
      <MoveReasonDialog
        open={moveReasonOpen}
        onOpenChange={(open) => { if (!open && pendingReasonMoveRef.current) handleMoveReasonCancel(); }}
        onConfirm={handleMoveReasonConfirm}
        onCancel={handleMoveReasonCancel}
        fromTechName={moveReasonMeta?.fromTechName}
        toTechName={moveReasonMeta?.toTechName}
        ticketLabel={moveReasonMeta?.ticketLabel}
      />

      {/* A deep link to a ticket no Dayboard date can show: its handoff
          context + Accept, and the FOH queue — never a dead end. */}
      <TicketDeepLinkFallback fallback={deepLinkFallback} onClose={clearDeepLinkFallback} />
      {/* Oct 20 addendum (F2): the card-less ticket's Command Center. The card itself stays hidden; its highlighted
          mount consumes openDetails and opens the same TicketDetailsPopup every Dayboard card opens. */}
      {deepLinkCcHostRow && (
        <div className="hidden" aria-hidden="true" data-testid="deep-link-command-center-host">
          <TicketCard
            key={deepLinkCcHostRow.id}
            ticket={{
              ...deepLinkCcHostRow,
              jobs: deepLinkCcHostRow.jobs,
              assignedTechId: deepLinkCcHostRow.assigned_tech_id,
              customerName: deepLinkCcHostRow.customer_name,
              customerPhone: deepLinkCcHostRow.customer_phone,
              dueOut: deepLinkCcHostRow.due_out,
              isComeback: deepLinkCcHostRow.is_comeback,
              startTime: formatShopTime(deepLinkCcHostRow.start_time, shopTimezone, 'short'),
              predictedMinutes: deepLinkCcHostRow.predicted_minutes,
              pauseReason: deepLinkCcHostRow.pause_reason,
              actualMinutes: deepLinkCcHostRow.actual_minutes,
            }}
            technician={technicians.find((t) => t.id === deepLinkCcHostRow.assigned_tech_id)}
            isInParkingLot={isOnBacklogBoard(deepLinkCcHostRow)}
            isHighlighted={true}
            technicians={technicians.map((t) => ({ id: t.id, name: t.name, is_present: t.is_present }))}
            hideTimerControls={true}
            hideKebabMenu={true}
            onTicketUpdated={() => refreshSingleTicket(deepLinkCcHostRow.id)}
          />
        </div>
      )}
      </div>
    </div>
    </ReminderCountsProvider>
  );
};

export default Dashboard;
